import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Camera, CheckCircle2, ChevronDown, ChevronUp, Clock3, Crown, Flag, Heart, Medal, PlayCircle, RefreshCcw, Search, Trash2, Trophy, Upload, UserPlus, Video, X } from 'lucide-react';
import ckoLiveLogo from '../ckopng/ckow.png';
import { CKO2026_TRICKS } from '../constants';
import { isLocalBackend, isNativeApp } from '../apiEnv';
import {
  CKO2026_WARMUP_MAX_VIDEO_SIZE,
  CKO2026_WARMUP_MODE_NAME,
  CKO2026_WARMUP_REMOTE_SOURCE_MAX_VIDEO_SIZE,
  fetchCko2026WarmupLeaderboard,
  fetchCko2026WarmupLeaderboardEntry,
  fetchCko2026WarmupUploadConfig,
  getCko2026WarmupCompletedCount,
  getCko2026WarmupTotalScore,
  groupCko2026WarmupTricksByScore,
  likeCko2026WarmupSubmission,
  normalizeCko2026WarmupProfile,
  registerForCko2026Warmup,
  reportCko2026WarmupSubmission,
  deleteOwnCko2026WarmupSubmission,
  fetchMyCko2026WarmupProfile,
  unreportCko2026WarmupSubmission,
  uploadCko2026WarmupVideo
} from '../cko2026Warmup';
import type { Cko2026WarmupLeaderboardEntry, Cko2026WarmupUploadConfig, TournamentTrick, User } from '../types';
import { canBrowserProcessVideoForUpload, compressVideoBlobToMaxSize, exportTrimmedVideoBlob, transcodeVideoBlobForWebPlayback } from '../videoTrimExport';

interface CKO2026WarmupModeProps {
  user: User;
  onBack: () => void;
  onUpdateUser: (user: User) => void;
  page?: 'overview' | 'my-tricks' | 'player-detail';
}

const WARMUP_TRICKS = CKO2026_TRICKS.filter((trick) => trick.score >= 1);
const TOTAL_AVAILABLE_SCORE = WARMUP_TRICKS.reduce((sum, trick) => sum + trick.score, 0);
const WARMUP_TRICK_COUNT = WARMUP_TRICKS.length;
const trickGroups = groupCko2026WarmupTricksByScore();
const RECORD_TRIM_MIN_DURATION = 0.5;
const RECORD_TRIM_FRAME_COUNT = 9;
const WARMUP_UPLOAD_DISABLED_MESSAGE = '预热赛维护中，暂时无法上传';

const formatTime = (timestamp: number | null | undefined) => {
  if (!timestamp) return '未记录';
  try {
    return new Date(timestamp).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '未记录';
  }
};

const getAvatarInitial = (name: string) => {
  const trimmed = name.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : 'U';
};

const clampVideoTime = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const isLikelyVideoFile = (file: File) => file.type.startsWith('video/') || /\.(mp4|m4v|mov|webm|ogg|ogv|mkv)$/i.test(file.name);

const shouldTranscodeWarmupUploadFile = (file: File) => {
  const normalizedType = file.type.split(';')[0].trim().toLowerCase();
  if (normalizedType === 'video/quicktime') {
    return true;
  }

  return /\.mov$/i.test(file.name);
};

const canUseServerSideWarmupSourceUpload = (file: File) => {
  if (typeof window === 'undefined') return false;
  // 本地 Node 后端没有 OSS 直传通道；线上和原生 App 才有。
  // 注意不能用 hostname 判断：原生 App 的页面也是 Capacitor 内置 localhost。
  return !isLocalBackend()
    && file.size > CKO2026_WARMUP_MAX_VIDEO_SIZE
    && file.size <= CKO2026_WARMUP_REMOTE_SOURCE_MAX_VIDEO_SIZE;
};
const canAccessWarmupAdmin = (user: User) =>
  !!(user.isAdmin || user.roles?.includes('administrator') || user.roles?.includes('author'));
const canUseWarmupBatchActions = (user: User) =>
  !!(user.isAdmin || user.roles?.includes('administrator'));

const isQuickTimeMimeType = (mimeType: string | undefined) => (mimeType || '').split(';')[0].trim().toLowerCase() === 'video/quicktime';
const getSubmissionStatus = (submission: ReturnType<typeof normalizeCko2026WarmupProfile>['submissions'][number] | undefined) =>
  !submission ? 'pending' : (submission.status || (submission.videoUrl ? 'ready' : 'processing'));
const WARMUP_UPLOAD_EXT_BY_MIME: Record<string, string> = {
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'video/quicktime': 'mov',
  'video/ogg': 'ogv',
  'video/x-matroska': 'mkv'
};

const createWarmupUploadFile = (blob: Blob, originalName: string) => {
  const normalizedMimeType = blob.type.split(';')[0].trim() || 'video/mp4';
  const fallbackBaseName = originalName.replace(/\.[^.]+$/, '') || `warmup-video-${Date.now()}`;
  const fallbackOriginalExt = originalName.includes('.') ? originalName.split('.').pop() || 'mp4' : 'mp4';
  const ext = WARMUP_UPLOAD_EXT_BY_MIME[normalizedMimeType] || fallbackOriginalExt;
  return new File([blob], `${fallbackBaseName}.${ext}`, {
    type: normalizedMimeType,
    lastModified: Date.now()
  });
};

const formatTrimClock = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return '00:00.0';
  const totalSeconds = Math.max(0, value);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds - minutes * 60;
  return `${String(minutes).padStart(2, '0')}:${seconds.toFixed(1).padStart(4, '0')}`;
};

const getWarmupNoticeType = (notice: { noticeType?: 'removal' | 'prompt' }) => notice.noticeType === 'prompt' ? 'prompt' : 'removal';
const getWarmupNoticeStyle = (notice: { noticeType?: 'removal' | 'prompt' }) => {
  if (getWarmupNoticeType(notice) === 'prompt') {
    return {
      wrapper: 'border-yellow-400/20 bg-yellow-500/10',
      icon: 'text-yellow-300',
      title: 'text-yellow-200',
      card: 'border-yellow-400/15 bg-yellow-500/5',
      label: '管理员提示：'
    } as const;
  }

  return {
    wrapper: 'border-red-400/20 bg-red-500/10',
    icon: 'text-red-400',
    title: 'text-red-300',
    card: 'border-red-400/15 bg-red-500/5',
    label: '移除理由：'
  } as const;
};

interface WarmupUploadPanelProps {
  profile: ReturnType<typeof normalizeCko2026WarmupProfile>;
  submissionMap: Map<string, ReturnType<typeof normalizeCko2026WarmupProfile>['submissions'][number]>;
  canUseBatchActions: boolean;
  uploadsEnabled: boolean;
  uploadingTrickId: string | null;
  deletingTrickId: string | null;
  selectedUploadScore: number | null;
  onSelectScore: (score: number | null) => void;
  onUpload: (trick: TournamentTrick, file: File | null) => void;
  onUploadDisabledAttempt: () => void;
  onDelete: (trick: TournamentTrick) => void;
  onStartCamera: (trickId: string, score: number) => void;
  isRecording: boolean;
  uploadProgress: number | null;
  processingProgress: number | null;
  processingLabel: string | null;
  batchOperation: { type: 'upload' | 'delete'; current: number; total: number } | null;
  onBatchUpload: (items: Array<{ trick: TournamentTrick; file: File }>) => void;
  onBatchDelete: (tricks: TournamentTrick[]) => void;
  onSetActionMessage: (message: string) => void;
}

function WarmupUploadPanel({
  profile,
  submissionMap,
  canUseBatchActions,
  uploadsEnabled,
  uploadingTrickId,
  deletingTrickId,
  selectedUploadScore,
  onSelectScore,
  onUpload,
  onUploadDisabledAttempt,
  onDelete,
  isRecording,
  uploadProgress,
  processingProgress,
  processingLabel,
  batchOperation,
  onBatchUpload,
  onBatchDelete,
  onSetActionMessage
}: WarmupUploadPanelProps) {
  const lastHandledUploadSelectionRef = useRef<{ key: string; ts: number } | null>(null);
  const batchUploadInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedTrickIds, setSelectedTrickIds] = useState<string[]>([]);

  const handleDisabledUploadClick = useCallback((event: React.MouseEvent<HTMLLabelElement | HTMLInputElement>) => {
    if (uploadsEnabled) return;
    event.preventDefault();
    event.stopPropagation();
    onUploadDisabledAttempt();
  }, [onUploadDisabledAttempt, uploadsEnabled]);

  const handleUploadInputSelection = useCallback((
    event: React.ChangeEvent<HTMLInputElement> | React.FormEvent<HTMLInputElement>,
    trick: TournamentTrick
  ) => {
    const input = event.currentTarget;
    const file = input.files?.[0] ?? null;
    if (!file) {
      input.value = '';
      return;
    }

    const selectionKey = `${trick.id}:${file.name}:${file.size}:${file.lastModified}`;
    const now = Date.now();
    const lastHandled = lastHandledUploadSelectionRef.current;
    // iPhone Safari may fire `input` and `change` together for the same picker result.
    // Keep the dedupe window short so users can immediately retry the same file.
    if (lastHandled && lastHandled.key === selectionKey && now - lastHandled.ts < 250) {
      input.value = '';
      return;
    }

    lastHandledUploadSelectionRef.current = { key: selectionKey, ts: now };
    onUpload(trick, file);
    input.value = '';
  }, [onUpload]);

  useEffect(() => {
    setSelectedTrickIds([]);
    if (batchUploadInputRef.current) {
      batchUploadInputRef.current.value = '';
    }
  }, [selectedUploadScore]);

  const toggleTrickSelection = useCallback((trickId: string) => {
    setSelectedTrickIds((prev) => (
      prev.includes(trickId)
        ? prev.filter((id) => id !== trickId)
        : [...prev, trickId]
    ));
  }, []);

  const handleBatchUploadInputSelection = useCallback((
    event: React.ChangeEvent<HTMLInputElement>,
    uploadCandidates: TournamentTrick[]
  ) => {
    const input = event.currentTarget;
    const files = Array.from(input.files ?? []);
    if (uploadCandidates.length === 0) {
      onSetActionMessage('当前选中的招式没有可批量上传的条目。');
      input.value = '';
      return;
    }
    if (files.length !== uploadCandidates.length) {
      onSetActionMessage(`请按招式顺序选择 ${uploadCandidates.length} 个视频文件，当前选择了 ${files.length} 个。`);
      input.value = '';
      return;
    }
    onBatchUpload(uploadCandidates.map((trick, index) => ({ trick, file: files[index] })));
    setSelectedTrickIds([]);
    input.value = '';
  }, [onBatchUpload, onSetActionMessage]);

  return (
  <div className="rounded-[24px] border border-white/10 bg-zinc-950/70 p-3 md:p-4">
    <div className="mb-3 flex items-center justify-between gap-3">
      <div>
        <h2 className="text-xl font-orbitron font-bold tracking-[0.12em] text-white">我的招式上传</h2>
        <p className="mt-1 text-xs text-zinc-500">按 CKO2026 官方招式表逐项上传，上传即计分。</p>
      </div>
      <div className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-2.5 py-1 text-[11px] font-orbitron uppercase tracking-[0.16em] text-cyan-100">
        Personal
      </div>
    </div>

    {!profile.isRegistered ? (
      <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.03] px-4 py-6 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-emerald-400/20 bg-emerald-500/10 text-emerald-200">
          <UserPlus size={24} />
        </div>
        <div className="mt-3 text-base font-semibold text-white">先报名，再开始上传视频</div>
        <p className="mt-2 text-sm leading-6 text-zinc-500">
          你首次进入此模式时需要先点击报名按钮。报名后会自动加入排行榜，并解锁个人招式上传界面。
        </p>
      </div>
    ) : (
      <div className="grid grid-cols-3 gap-1.5 min-[400px]:grid-cols-4 sm:grid-cols-5 sm:gap-2">
        {trickGroups.map((group) => {
          const completedInGroup = group.tricks.filter((trick) => submissionMap.has(trick.id)).length;
          const progressPct = Math.round((completedInGroup / group.tricks.length) * 100);
          return (
            <button
              key={group.score}
              type="button"
              onClick={() => onSelectScore(group.score)}
              className="flex flex-col items-center justify-center gap-1 rounded-2xl border border-white/10 bg-zinc-900 px-2 py-3 text-center transition-colors hover:border-cyan-400/30 hover:bg-zinc-800"
            >
              <div className="text-lg font-orbitron font-bold text-white">{group.score}</div>
              <div className="text-[9px] uppercase tracking-[0.12em] text-zinc-500">级</div>
              <div className="mt-0.5 h-1 w-full overflow-hidden rounded-full bg-white/[0.06]">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-cyan-500/60 to-cyan-400/60 transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <div className="text-[10px] text-zinc-500">{completedInGroup}/{group.tricks.length}</div>
            </button>
          );
        })}
      </div>
    )}

    {!uploadsEnabled && (
      <div className="mt-3 rounded-2xl border border-amber-400/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
        {WARMUP_UPLOAD_DISABLED_MESSAGE}
      </div>
    )}

    {selectedUploadScore !== null && (() => {
      const group = trickGroups.find((g) => g.score === selectedUploadScore);
      if (!group) return null;
      const selectedTricks = group.tricks.filter((trick) => selectedTrickIds.includes(trick.id));
      const uploadCandidates = selectedTricks.filter((trick) => !submissionMap.has(trick.id));
      const deleteCandidates = selectedTricks.filter((trick) => submissionMap.has(trick.id));
      const allSelected = group.tricks.length > 0 && selectedTricks.length === group.tricks.length;
      const isPanelBusy = !!uploadingTrickId || !!deletingTrickId || !!batchOperation || isRecording;
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => onSelectScore(null)}>
          <div
            className="w-full max-w-2xl max-h-[85vh] overflow-y-auto overflow-x-hidden rounded-2xl border border-zinc-800 bg-zinc-950 p-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <div className="text-sm font-semibold text-white">{selectedUploadScore} 级招式</div>
                <div className="text-[11px] text-zinc-500">共 {group.tricks.length} 个招式</div>
              </div>
              <button
                type="button"
                onClick={() => onSelectScore(null)}
                className="fixed z-50 top-20 xl:top-24 right-4 inline-flex items-center justify-center h-10 w-10 rounded-2xl border-2 border-zinc-600 bg-zinc-800/90 text-white shadow-lg backdrop-blur-sm transition-all hover:bg-zinc-700/90 hover:border-zinc-400 active:scale-95"
              >
                <X size={18} />
              </button>
            </div>

            {canUseBatchActions && (
              <div className="mb-3 rounded-2xl border border-white/10 bg-black/20 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-white">批量操作</div>
                    <div className="mt-1 text-[11px] leading-5 text-zinc-500">
                      已选 {selectedTricks.length} 个招式。批量上传会按本列表招式顺序，依次对应你选择的视频文件。
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedTrickIds(allSelected ? [] : group.tricks.map((trick) => trick.id))}
                      disabled={isPanelBusy}
                      className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-zinc-200 transition-colors hover:bg-white/10 disabled:opacity-50"
                    >
                      {allSelected ? '取消全选' : '全选'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedTrickIds([])}
                      disabled={selectedTricks.length === 0 || isPanelBusy}
                      className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-zinc-200 transition-colors hover:bg-white/10 disabled:opacity-50"
                    >
                      清空
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!uploadsEnabled) {
                          onSetActionMessage(WARMUP_UPLOAD_DISABLED_MESSAGE);
                          return;
                        }
                        if (uploadCandidates.length === 0) {
                          onSetActionMessage('请先勾选未上传的招式，再执行批量上传。');
                          return;
                        }
                        if (isPanelBusy) {
                          return;
                        }
                        batchUploadInputRef.current?.click();
                      }}
                      disabled={uploadCandidates.length === 0 || isPanelBusy || !uploadsEnabled}
                      className="rounded-xl border border-cyan-400/20 bg-cyan-500/10 px-3 py-2 text-xs font-semibold text-cyan-100 transition-colors hover:bg-cyan-500/15 disabled:opacity-50"
                    >
                      {batchOperation?.type === 'upload'
                        ? `批量上传中 ${batchOperation.current}/${batchOperation.total}`
                        : `批量上传 ${uploadCandidates.length > 0 ? `(${uploadCandidates.length})` : ''}`}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (deleteCandidates.length === 0) {
                          onSetActionMessage('请先勾选已有视频的招式，再执行批量删除。');
                          return;
                        }
                        if (isPanelBusy) {
                          return;
                        }
                        onBatchDelete(deleteCandidates);
                        setSelectedTrickIds([]);
                      }}
                      disabled={deleteCandidates.length === 0 || isPanelBusy}
                      className="rounded-xl border border-red-400/20 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-100 transition-colors hover:bg-red-500/15 disabled:opacity-50"
                    >
                      {batchOperation?.type === 'delete'
                        ? `批量删除中 ${batchOperation.current}/${batchOperation.total}`
                        : `批量删除 ${deleteCandidates.length > 0 ? `(${deleteCandidates.length})` : ''}`}
                    </button>
                  </div>
                </div>
                <input
                  ref={batchUploadInputRef}
                  type="file"
                  multiple
                  accept="video/*,.mp4,.m4v,.mov,.webm,.ogg,.ogv,.mkv"
                  className="hidden"
                  onChange={(event) => handleBatchUploadInputSelection(event, uploadCandidates)}
                />
              </div>
            )}

            <div className="flex flex-col gap-2.5">
              {group.tricks.map((trick) => {
                const submission = submissionMap.get(trick.id);
                const submissionStatus = getSubmissionStatus(submission);
                const isUploading = uploadingTrickId === trick.id;
                const isDeleting = deletingTrickId === trick.id;
                const isBusy = !!uploadingTrickId || !!deletingTrickId || !!batchOperation;
                const canShowUploadActions = !submission || isUploading;
                const isSelected = selectedTrickIds.includes(trick.id);
                const localProgress = isUploading ? (uploadProgress ?? processingProgress) : null;
                const localStageLabel = uploadProgress != null
                  ? '上传中'
                  : processingLabel === '压缩进度'
                    ? '压缩中'
                    : processingLabel === '转码进度'
                      ? '转码中'
                      : '准备中';
                return (
                  <div key={trick.id} className="relative overflow-hidden rounded-xl border border-white/10 bg-zinc-900 p-2.5">
                    {isUploading && localProgress != null && (
                      <>
                        <div
                          className="pointer-events-none absolute inset-y-0 left-0 bg-gradient-to-r from-cyan-500/20 via-sky-400/24 to-cyan-300/18 transition-all duration-300"
                          style={{ width: `${localProgress}%` }}
                        />
                        <div
                          className="pointer-events-none absolute inset-y-0 left-0 w-24 -translate-x-full bg-gradient-to-r from-transparent via-white/18 to-transparent blur-sm"
                          style={{
                            animation: 'warmup-upload-sheen 1.6s linear infinite',
                            animationPlayState: localProgress > 0 && localProgress < 100 ? 'running' : 'paused'
                          }}
                        />
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-cyan-500/[0.05] via-transparent to-sky-400/[0.03]" />
                      </>
                    )}
                    <div className="relative z-[1] flex items-start justify-between gap-2.5">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="rounded-lg border border-yellow-400/20 bg-yellow-500/10 px-2 py-0.5 text-xs font-orbitron text-yellow-100">
                            {trick.score}分
                          </div>
                          {isUploading ? (
                            <div className="inline-flex items-center gap-1 rounded-lg border border-cyan-400/20 bg-cyan-500/10 px-2 py-0.5 text-xs text-cyan-100">
                              <RefreshCcw size={13} className="animate-spin" />
                              {localStageLabel}
                            </div>
                          ) : submissionStatus === 'ready' ? (
                            <div className="inline-flex items-center gap-1 rounded-lg border border-emerald-400/20 bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-100">
                              <CheckCircle2 size={13} />
                              已完成
                            </div>
                          ) : submissionStatus === 'processing' ? (
                            <div className="inline-flex items-center gap-1 rounded-lg border border-cyan-400/20 bg-cyan-500/10 px-2 py-0.5 text-xs text-cyan-100">
                              <RefreshCcw size={13} className="animate-spin" />
                              处理中
                            </div>
                          ) : submission ? (
                            <div className="inline-flex items-center gap-1 rounded-lg border border-amber-400/20 bg-amber-500/10 px-2 py-0.5 text-xs text-amber-100">
                              <AlertTriangle size={13} />
                              处理失败
                            </div>
                          ) : (
                            <div className="inline-flex items-center gap-1 rounded-lg border border-zinc-700 bg-zinc-800 px-2 py-0.5 text-xs text-zinc-400">
                              <Clock3 size={13} />
                              待上传
                            </div>
                          )}
                        </div>
                        <div className="mt-2 whitespace-pre-line text-sm leading-5 text-white">{trick.name}</div>
                        {submission && (
                          <div className="mt-2 text-xs text-zinc-500">
                            最近上传：{formatTime(submission.uploadedAt)}
                          </div>
                        )}
                        {isUploading && (
                          <div className="mt-2 rounded-lg border border-cyan-400/20 bg-cyan-500/10 px-2.5 py-1.5 text-[11px] leading-5 text-cyan-100">
                            <div>{uploadProgress != null ? '视频正在上传到服务器，完成后会自动进入转码队列。' : '视频正在本地处理，请保持页面开启并稍等。'}</div>
                            {localProgress != null && (
                              <div className="mt-2">
                                <div className="mb-1 flex items-center justify-between">
                                  <span className="text-[10px] text-cyan-200/70">{uploadProgress != null ? '上传进度' : (processingLabel || '处理进度')}</span>
                                  <span className="text-[10px] font-mono text-cyan-300">{localProgress}%</span>
                                </div>
                                <div className="h-1.5 w-full overflow-hidden rounded-full bg-cyan-500/20">
                                  <div
                                    className="h-full rounded-full bg-cyan-400 transition-all duration-300"
                                    style={{ width: `${localProgress}%` }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {submission?.processingMessage && submissionStatus !== 'ready' && (
                          <div className="mt-2 rounded-lg border border-cyan-400/20 bg-cyan-500/10 px-2.5 py-1.5 text-[11px] leading-5 text-cyan-100">
                            {submission.processingMessage}
                          </div>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-3 pt-0.5">
                        {canUseBatchActions && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isBusy || isRecording}
                            onChange={() => toggleTrickSelection(trick.id)}
                            className="h-4 w-4 rounded border-white/20 bg-zinc-900 text-cyan-400 accent-cyan-400 disabled:opacity-50"
                            aria-label={`选择 ${trick.name}`}
                          />
                        )}
                        {canShowUploadActions && (
                          <>
                            <label
                              title={!uploadsEnabled ? WARMUP_UPLOAD_DISABLED_MESSAGE : (isUploading ? '上传中...' : '上传视频')}
                              onClick={handleDisabledUploadClick}
                              className={`relative inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border transition-colors overflow-hidden ${
                                isUploading
                                  ? 'border-cyan-300/30 bg-cyan-500/15 text-cyan-100'
                                  : !uploadsEnabled
                                    ? 'border-amber-400/20 bg-amber-500/10 text-amber-100'
                                  : isBusy
                                    ? 'border-cyan-400/20 bg-cyan-500/10 text-cyan-50 opacity-60'
                                    : 'border-cyan-400/20 bg-cyan-500/10 text-cyan-50 hover:border-cyan-300/40 hover:bg-cyan-500/15'
                              } ${isBusy && !isUploading ? 'pointer-events-none' : ''}`}
                            >
                              <Upload size={15} className={isUploading ? 'animate-pulse' : ''} />
                              <span className="sr-only">{isUploading ? '上传中...' : '上传视频'}</span>
                              <input
                                type="file"
                                accept="video/*,.mp4,.m4v,.mov,.webm,.ogg,.ogv,.mkv"
                                className="absolute inset-0 z-10 cursor-pointer opacity-0"
                                disabled={isUploading || !uploadsEnabled}
                                onClick={handleDisabledUploadClick}
                                onInput={(event) => handleUploadInputSelection(event, trick)}
                                onChange={(event) => handleUploadInputSelection(event, trick)}
                              />
                            </label>
                          </>
                        )}
                        {submission && (
                          <button
                            type="button"
                            title={isDeleting ? '删除中...' : '删除视频'}
                            onClick={(event) => {
                              event.stopPropagation();
                              onDelete(trick);
                            }}
                            disabled={isDeleting || isUploading || isRecording}
                            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-red-400/20 bg-red-500/10 text-red-100 transition-colors hover:border-red-300/40 hover:bg-red-500/15 disabled:opacity-40"
                          >
                            <Trash2 size={15} />
                            <span className="sr-only">{isDeleting ? '删除中...' : '删除视频'}</span>
                          </button>
                        )}
                      </div>
                    </div>
                    {submission?.videoUrl && (
                      <video
                        className="mt-2.5 aspect-video w-full rounded-xl border border-white/10 bg-black"
                        src={submission.videoUrl}
                        controls
                        preload="metadata"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    })()}

    <div className="mt-3 rounded-2xl border border-white/10 bg-black/20 p-3 text-sm leading-6 text-zinc-400">
      <div className="mb-2 flex items-center gap-2 text-sm text-white">
        <Video size={16} className="text-cyan-300" />
        上传规则
      </div>
      <div>1. 每个招式仅按最新上传视频计分，同一招式重复上传不会重复叠加分数。</div>
      <div>2. 视频上传后会先进入处理队列，处理完成后才会更新总分、排行榜，并对其他用户显示可播放视频。</div>
      <div>3. 建议上传单个短视频片段。超过 {Math.round(CKO2026_WARMUP_MAX_VIDEO_SIZE / 1024 / 1024)}MB 的大视频会优先尝试服务端分片上传并自动转码，必要时浏览器会先压缩处理。</div>
    </div>
  </div>
  );
}

interface VideoTrimTimelineProps {
  duration: number;
  trimStart: number;
  trimEnd: number;
  currentTime: number;
  thumbnails: string[];
  onTrimStartChange: (value: number) => void;
  onTrimEndChange: (value: number) => void;
  onSeek: (value: number) => void;
}

function VideoTrimTimeline({
  duration,
  trimStart,
  trimEnd,
  currentTime,
  thumbnails,
  onTrimStartChange,
  onTrimEndChange,
  onSeek
}: VideoTrimTimelineProps) {
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const dragModeRef = useRef<'start' | 'end' | 'playhead' | null>(null);
  const ignoreClickRef = useRef(false);

  // Stable refs so drag callbacks never change identity during drag
  const trimStartRef = useRef(trimStart);
  const trimEndRef = useRef(trimEnd);
  const safeDurationRef = useRef(Math.max(duration, RECORD_TRIM_MIN_DURATION));
  const onTrimStartChangeRef = useRef(onTrimStartChange);
  const onTrimEndChangeRef = useRef(onTrimEndChange);
  const onSeekRef = useRef(onSeek);

  useEffect(() => { trimStartRef.current = trimStart; }, [trimStart]);
  useEffect(() => { trimEndRef.current = trimEnd; }, [trimEnd]);
  useEffect(() => { safeDurationRef.current = Math.max(duration, RECORD_TRIM_MIN_DURATION); }, [duration]);
  useEffect(() => { onTrimStartChangeRef.current = onTrimStartChange; }, [onTrimStartChange]);
  useEffect(() => { onTrimEndChangeRef.current = onTrimEndChange; }, [onTrimEndChange]);
  useEffect(() => { onSeekRef.current = onSeek; }, [onSeek]);

  const safeDuration = Math.max(duration, RECORD_TRIM_MIN_DURATION);
  const startPct = (trimStart / safeDuration) * 100;
  const endPct = (trimEnd / safeDuration) * 100;
  const playheadTime = clampVideoTime(currentTime, trimStart, trimEnd);
  const playheadPct = (playheadTime / safeDuration) * 100;

  // All callbacks are stable (empty deps) — they read latest values from refs
  const updateFromClientX = useCallback((clientX: number, mode: 'start' | 'end' | 'playhead') => {
    if (!timelineRef.current) return;
    const rect = timelineRef.current.getBoundingClientRect();
    if (!rect.width) return;

    const dur = safeDurationRef.current;
    const ratio = clampVideoTime((clientX - rect.left) / rect.width, 0, 1);
    const nextTime = ratio * dur;

    if (mode === 'start') {
      const nextStart = clampVideoTime(nextTime, 0, Math.max(0, trimEndRef.current - RECORD_TRIM_MIN_DURATION));
      onTrimStartChangeRef.current(nextStart);
      onSeekRef.current(nextStart);
      return;
    }

    if (mode === 'end') {
      const nextEnd = clampVideoTime(nextTime, Math.min(dur, trimStartRef.current + RECORD_TRIM_MIN_DURATION), dur);
      onTrimEndChangeRef.current(nextEnd);
      onSeekRef.current(nextEnd);
      return;
    }

    onSeekRef.current(clampVideoTime(nextTime, trimStartRef.current, trimEndRef.current));
  }, []);

  const handlePointerMove = useCallback((event: PointerEvent) => {
    const mode = dragModeRef.current;
    if (!mode) return;
    ignoreClickRef.current = true;
    updateFromClientX(event.clientX, mode);
  }, [updateFromClientX]);

  const stopDrag = useCallback(() => {
    dragModeRef.current = null;
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', stopDrag);
    window.removeEventListener('pointercancel', stopDrag);
  }, [handlePointerMove]);

  const beginDrag = useCallback((mode: 'start' | 'end' | 'playhead', clientX: number) => {
    dragModeRef.current = mode;
    ignoreClickRef.current = false;
    updateFromClientX(clientX, mode);
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopDrag);
    window.addEventListener('pointercancel', stopDrag);
  }, [handlePointerMove, stopDrag, updateFromClientX]);

  // Only cleanup on unmount, never during drag
  useEffect(() => () => {
    dragModeRef.current = null;
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', stopDrag);
    window.removeEventListener('pointercancel', stopDrag);
  }, []);

  const handleTimelineClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (ignoreClickRef.current) {
      ignoreClickRef.current = false;
      return;
    }
    updateFromClientX(event.clientX, 'playhead');
  }, [updateFromClientX]);

  return (
    <div className="rounded-[24px] border border-amber-300/15 bg-zinc-950/90 p-3 shadow-[0_20px_60px_rgba(0,0,0,0.35)] sm:rounded-[28px] sm:p-4">
      <div className="rounded-[22px] border border-white/8 bg-zinc-900/90 p-2.5 sm:rounded-[24px] sm:p-3">
        <div
          ref={timelineRef}
          className="relative h-16 overflow-hidden rounded-[18px] bg-zinc-950 select-none touch-none sm:h-20 sm:rounded-[20px]"
          onClick={handleTimelineClick}
        >
          <div className="absolute inset-0 flex">
            {(thumbnails.length ? thumbnails : Array.from({ length: RECORD_TRIM_FRAME_COUNT }, () => '')).map((thumbnail, index) => (
              <div key={`${thumbnail || 'placeholder'}-${index}`} className="relative h-full flex-1 overflow-hidden border-r border-black/20 last:border-r-0">
                {thumbnail ? (
                  <img src={thumbnail} alt="" className="h-full w-full object-cover opacity-85" draggable={false} />
                ) : (
                  <div className="h-full w-full bg-[linear-gradient(135deg,rgba(250,204,21,0.18),rgba(24,24,27,0.92),rgba(59,130,246,0.14))]" />
                )}
              </div>
            ))}
          </div>

          <div className="absolute inset-y-0 left-0 bg-black/60" style={{ width: `${startPct}%` }} />
          <div className="absolute inset-y-0 right-0 bg-black/60" style={{ width: `${Math.max(0, 100 - endPct)}%` }} />

          <div
            className="absolute inset-y-1 rounded-[18px] border-2 border-amber-300 bg-amber-300/8 shadow-[0_0_0_1px_rgba(250,204,21,0.22)]"
            style={{ left: `${startPct}%`, width: `${Math.max(1.5, endPct - startPct)}%` }}
          >
            <div className="absolute inset-x-0 top-1.5 border-t border-amber-200/40" />
            <div className="absolute inset-x-0 bottom-1.5 border-t border-amber-200/40" />
          </div>

          <button
            type="button"
            aria-label="调整起始时间"
            className="absolute top-1 bottom-1 z-20 flex w-4 -translate-x-1/2 cursor-ew-resize items-center justify-center rounded-full border border-amber-100/50 bg-amber-200/90 text-zinc-950 shadow-lg touch-none sm:w-5"
            style={{ left: `${startPct}%` }}
            onPointerDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              beginDrag('start', event.clientX);
            }}
          >
            <span className="h-6 w-1 rounded-full bg-zinc-900/70 sm:h-7" />
          </button>

          <button
            type="button"
            aria-label="调整结束时间"
            className="absolute top-1 bottom-1 z-20 flex w-4 -translate-x-1/2 cursor-ew-resize items-center justify-center rounded-full border border-amber-100/50 bg-amber-200/90 text-zinc-950 shadow-lg touch-none sm:w-5"
            style={{ left: `${endPct}%` }}
            onPointerDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              beginDrag('end', event.clientX);
            }}
          >
            <span className="h-6 w-1 rounded-full bg-zinc-900/70 sm:h-7" />
          </button>

          <div className="pointer-events-none absolute inset-y-0 z-10 w-px -translate-x-1/2 bg-white/90 shadow-[0_0_18px_rgba(255,255,255,0.75)]" style={{ left: `${playheadPct}%` }} />
          <button
            type="button"
            aria-label="拖动播放头"
            className="absolute bottom-1 z-30 h-3.5 w-3.5 -translate-x-1/2 rounded-full border border-white/80 bg-white shadow-[0_0_20px_rgba(255,255,255,0.6)] touch-none sm:h-4 sm:w-4"
            style={{ left: `${playheadPct}%` }}
            onPointerDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              beginDrag('playhead', event.clientX);
            }}
          />
        </div>

      </div>
    </div>
  );
}

export function CKO2026WarmupMode({ user, onBack, onUpdateUser, page = 'overview' }: CKO2026WarmupModeProps) {
  const navigate = useNavigate();
  const params = useParams<{ userId?: string }>();
  const isMyTricksPage = page === 'my-tricks';
  const isPlayerDetailPage = page === 'player-detail';
  const [leaderboard, setLeaderboard] = useState<Cko2026WarmupLeaderboardEntry[]>([]);
  const [isLeaderboardLoading, setIsLeaderboardLoading] = useState(true);
  const [leaderboardVersion, setLeaderboardVersion] = useState(0);
  const [isLeaderboardAnimating, setIsLeaderboardAnimating] = useState(false);
  const [leaderboardError, setLeaderboardError] = useState('');
  const [selectedLeaderboardDetail, setSelectedLeaderboardDetail] = useState<Cko2026WarmupLeaderboardEntry | null>(null);
  const [isSelectedLeaderboardLoading, setIsSelectedLeaderboardLoading] = useState(false);
  const [selectedLeaderboardError, setSelectedLeaderboardError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [warmupUploadConfig, setWarmupUploadConfig] = useState<Cko2026WarmupUploadConfig>({ uploadsEnabled: true, updatedAt: 0 });
  const [overlayError, setOverlayError] = useState('');
  // Track dismissed notices by "trickId-removedAt" key, persisted across the session
  const [dismissedNoticeKeys, setDismissedNoticeKeys] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('cko2026_warmup_dismissed_notices');
      if (raw) return new Set(JSON.parse(raw));
    } catch { }
    return new Set();
  });
  const [isRegistering, setIsRegistering] = useState(false);
  const [uploadingTrickId, setUploadingTrickId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [processingProgress, setProcessingProgress] = useState<number | null>(null);
  const [processingLabel, setProcessingLabel] = useState<string | null>(null);
  const [selectedUploadScore, setSelectedUploadScore] = useState<number | null>(null);
  // Camera device selection
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);
  const [showCameraPicker, setShowCameraPicker] = useState(false);
  const [pendingCameraTrickId, setPendingCameraTrickId] = useState<string | null>(null);
  const [pendingCameraScore, setPendingCameraScore] = useState<number | null>(null);
  // Camera recording state
  const [recordState, setRecordState] = useState<'idle' | 'requesting' | 'countdown' | 'recording' | 'preview' | 'trimming' | 'confirm' | 'submitting'>('idle');
  const [recordTrickId, setRecordTrickId] = useState<string | null>(null);
  const [recordScore, setRecordScore] = useState<number | null>(null);
  const [countdown, setCountdown] = useState(3);
  const [recordTime, setRecordTime] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(30);
  const [videoDuration, setVideoDuration] = useState(0);
  const [previewCurrentTime, setPreviewCurrentTime] = useState(0);
  const [trimThumbnails, setTrimThumbnails] = useState<string[]>([]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const captureRequestTokenRef = useRef(0);
  const cameraPickerBusyRef = useRef(false);
  // 录制代次：cleanupRecording 每次都会 +1，用来让排队中的 onstop/ondataavailable 失效
  const recordSessionRef = useRef(0);
  // 当前录像的 objectURL 快照，供卸载清理使用（state 在闭包里会过期）
  const recordedUrlRef = useRef<string | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const livePreviewRef = useRef<HTMLVideoElement | null>(null);
  const videoPreviewRef = useRef<HTMLVideoElement | null>(null);
  const seekGuardRef = useRef(false);
  const countdownTimerRef = useRef<number | null>(null);
  const recordTimerRef = useRef<number | null>(null);
  const recordTimeRef = useRef(0);
  const latestUserRef = useRef(user);
  const profile = useMemo(() => normalizeCko2026WarmupProfile(user.cko2026Warmup), [user.cko2026Warmup]);
  const canBypassWarmupUploadToggle = useMemo(() => canAccessWarmupAdmin(user), [user]);
  const canUseBatchActions = useMemo(() => canUseWarmupBatchActions(user), [user]);
  const uploadsEnabledForCurrentUser = warmupUploadConfig.uploadsEnabled || canBypassWarmupUploadToggle;
  const [isHeroCardExpanded, setIsHeroCardExpanded] = useState(!profile.isRegistered);
  const [isTricksSectionExpanded, setIsTricksSectionExpanded] = useState(false);
  const [selectedVideoScore, setSelectedVideoScore] = useState<number | null>(null);
  const [floatPos, setFloatPos] = useState({ x: 0, y: 0 });
  const [floatSide, setFloatSide] = useState<'left' | 'right'>('right');
  const floatPosRef = useRef(floatPos);
  const floatSideRef = useRef(floatSide);
  const dragRef = useRef({ startX: 0, startY: 0, startPosX: 0, startPosY: 0, isDragging: false, hasMoved: false, buttonWidth: 44, buttonHeight: 44 });
  const [didDrag, setDidDrag] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMatchId, setSearchMatchId] = useState<string | null>(null);
  const [pendingDeleteTrick, setPendingDeleteTrick] = useState<TournamentTrick | null>(null);
  const [pendingBatchDeleteTricks, setPendingBatchDeleteTricks] = useState<TournamentTrick[]>([]);
  const [deletingTrickId, setDeletingTrickId] = useState<string | null>(null);
  const [batchOperation, setBatchOperation] = useState<{ type: 'upload' | 'delete'; current: number; total: number } | null>(null);
  const [pendingReport, setPendingReport] = useState<{ userId: string; trickId: string; trickName: string } | null>(null);
  const [reportReasonInput, setReportReasonInput] = useState('');
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const leaderboardListRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const completedCount = useMemo(() => getCko2026WarmupCompletedCount(profile), [profile]);
  const totalScore = useMemo(() => getCko2026WarmupTotalScore(profile), [profile]);
  const submissionMap = useMemo(
    () => new Map(profile.submissions.map((submission) => [submission.trickId, submission])),
    [profile.submissions]
  );
  const displayVideoDuration = useMemo(
    () => (Number.isFinite(videoDuration) && videoDuration > 0 ? videoDuration : Math.max(recordTime, 0)),
    [recordTime, videoDuration]
  );
  const hasProcessingSubmissions = useMemo(
    () => profile.submissions.some((submission) => getSubmissionStatus(submission) === 'processing'),
    [profile.submissions]
  );

  useEffect(() => {
    latestUserRef.current = user;
  }, [user]);

  useEffect(() => {
    setIsHeroCardExpanded(!profile.isRegistered);
  }, [profile.isRegistered]);

  const handleFloatMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setDidDrag(false);
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: rect.left,
      startPosY: rect.top,
      isDragging: true,
      hasMoved: false,
      buttonWidth: rect.width,
      buttonHeight: rect.height
    };
  }, []);

  const handleFloatTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    setDidDrag(false);
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    dragRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startPosX: rect.left,
      startPosY: rect.top,
      isDragging: true,
      hasMoved: false,
      buttonWidth: rect.width,
      buttonHeight: rect.height
    };
  }, []);

  useEffect(() => {
    const getTopMargin = () => window.innerWidth >= 1280 ? 72 : 52;
    const getBottomMargin = () => window.innerWidth >= 1280 ? 8 : 72;

    const onMove = (e: MouseEvent) => {
      if (!dragRef.current.isDragging) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      if (!dragRef.current.hasMoved) {
        if (Math.abs(dx) <= 3 && Math.abs(dy) <= 3) return;
        dragRef.current.hasMoved = true;
        setDidDrag(true);
      }
      const newY = dragRef.current.startPosY + dy;
      const topMargin = getTopMargin();
      const maxY = window.innerHeight - dragRef.current.buttonHeight - getBottomMargin();
      setFloatPos({
        x: dragRef.current.startPosX + dx,
        y: Math.max(topMargin, Math.min(newY, maxY))
      });
    };
    const onUp = () => {
      if (!dragRef.current.isDragging) return;
      const wasMoved = dragRef.current.hasMoved;
      dragRef.current.isDragging = false;
      if (!wasMoved) return;

      const { x, y } = floatPosRef.current;
      const { buttonWidth: bw, buttonHeight: bh } = dragRef.current;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const centerX = x + bw / 2;
      const topMargin = getTopMargin();
      const bottomMargin = getBottomMargin();

      const snapSide: 'left' | 'right' = centerX < vw / 2 ? 'left' : 'right';
      const snapX = snapSide === 'left' ? 8 : vw - bw - 8;
      const clampedY = Math.max(topMargin, Math.min(y, vh - bh - bottomMargin));

      setFloatPos({ x: snapX, y: clampedY });
      setFloatSide(snapSide);
      floatPosRef.current = { x: snapX, y: clampedY };
      floatSideRef.current = snapSide;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!dragRef.current.isDragging) return;
      e.preventDefault();
      const touch = e.touches[0];
      const dx = touch.clientX - dragRef.current.startX;
      const dy = touch.clientY - dragRef.current.startY;
      if (!dragRef.current.hasMoved) {
        if (Math.abs(dx) <= 3 && Math.abs(dy) <= 3) return;
        dragRef.current.hasMoved = true;
        setDidDrag(true);
      }
      const newY = dragRef.current.startPosY + dy;
      const topMargin = getTopMargin();
      const maxY = window.innerHeight - dragRef.current.buttonHeight - getBottomMargin();
      setFloatPos({
        x: dragRef.current.startPosX + dx,
        y: Math.max(topMargin, Math.min(newY, maxY))
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
  }, []);

  // Sync refs with state
  useEffect(() => {
    floatPosRef.current = floatPos;
  }, [floatPos]);
  useEffect(() => {
    floatSideRef.current = floatSide;
  }, [floatSide]);

  const handleFloatClick = useCallback(() => {
    if (didDrag) return;
    setIsHeroCardExpanded((v) => !v);
  }, [didDrag]);

  const loadLeaderboard = useCallback(async () => {
    setIsLeaderboardLoading(true);
    setLeaderboardError('');
    try {
      const entries = await fetchCko2026WarmupLeaderboard();
      setLeaderboard(entries);
      setLeaderboardVersion(v => v + 1);
    } catch (error) {
      setLeaderboardError((error as Error)?.message || '排行榜加载失败');
    } finally {
      setIsLeaderboardLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  useEffect(() => {
    let cancelled = false;
    fetchCko2026WarmupUploadConfig()
      .then((config) => {
        if (cancelled) return;
        setWarmupUploadConfig(config);
      })
      .catch(() => {
        if (cancelled) return;
        setWarmupUploadConfig({ uploadsEnabled: true, updatedAt: 0 });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!hasProcessingSubmissions) return;
    let cancelled = false;
    const syncProfile = async () => {
      try {
        const updatedProfile = await fetchMyCko2026WarmupProfile();
        if (cancelled) return;
        // 这里必须用 ref 里的最新 user，且不能把 user 放进依赖数组：
        // 一旦依赖 user，onUpdateUser 换掉 user 身份就会让 effect 重跑并立刻再请求一次，
        // 5 秒轮询会退化成「一次网络往返一轮」的请求风暴。
        const previousSnapshot = JSON.stringify(latestUserRef.current.cko2026Warmup ?? null);
        const nextSnapshot = JSON.stringify(updatedProfile ?? null);
        if (previousSnapshot !== nextSnapshot) {
          const nextUser = { ...latestUserRef.current, cko2026Warmup: updatedProfile };
          latestUserRef.current = nextUser;
          onUpdateUser(nextUser);
        }
        if (!updatedProfile.submissions.some((submission) => getSubmissionStatus(submission) === 'processing')) {
          await loadLeaderboard();
          setActionMessage('视频处理完成，列表与排行榜已更新。');
        }
      } catch {
        // Ignore polling errors and retry on next tick.
      }
    };

    syncProfile();
    const timer = window.setInterval(syncProfile, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [hasProcessingSubmissions, loadLeaderboard, onUpdateUser]);

  useEffect(() => {
    if (isLeaderboardLoading || leaderboardError || leaderboardVersion === 0) return;
    setIsLeaderboardAnimating(false);
    const timer = window.setTimeout(() => {
      setIsLeaderboardAnimating(true);
    }, 20);
    return () => window.clearTimeout(timer);
  }, [isLeaderboardLoading, leaderboardError, leaderboardVersion]);

  const getLeaderboardCardAnimationStyle = useCallback((delayMs: number) => {
    if (!isLeaderboardAnimating) {
      return {
        opacity: 0,
        transform: 'translateX(60px)'
      } as const;
    }
    return {
      animation: 'leaderboardSlideIn 0.2s ease-out forwards',
      animationDelay: `${delayMs}ms`,
      opacity: 0
    } as const;
  }, [isLeaderboardAnimating]);

  const dismissRemovalNotice = useCallback((trickId: string, removedAt: number) => {
    setDismissedNoticeKeys((prev) => {
      const next = new Set(prev);
      next.add(`${trickId}-${removedAt}`);
      try { localStorage.setItem('cko2026_warmup_dismissed_notices', JSON.stringify([...next])); } catch { }
      return next;
    });
  }, []);

  const activeRemovalNotices = useMemo(() => {
    if (!profile.removedNotices) return [];
    return profile.removedNotices.filter(
      (notice) => !dismissedNoticeKeys.has(`${notice.trickId}-${notice.removedAt}`)
    );
  }, [profile.removedNotices, dismissedNoticeKeys]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchMatchId(null);
      return;
    }
    const lowerQuery = query.toLowerCase();
    const firstMatch = leaderboard.find(entry =>
      entry.displayName.toLowerCase().includes(lowerQuery)
    );
    setSearchMatchId(firstMatch ? firstMatch.userId : null);
  }, [leaderboard]);

  const handleSuggestionClick = useCallback((entry: Cko2026WarmupLeaderboardEntry) => {
    setSearchMatchId(entry.userId);
    setSearchQuery(entry.displayName);
    setTimeout(() => {
      const el = leaderboardListRef.current?.querySelector(`[data-leaderboard-id="${entry.userId}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  }, []);

  const scrollToTop = useCallback(() => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const [likingMap, setLikingMap] = useState<Record<string, boolean>>({});
  const [likesCache, setLikesCache] = useState<Record<string, number>>({});
  const [reportingMap, setReportingMap] = useState<Record<string, boolean>>({});
  const [unreportingMap, setUnreportingMap] = useState<Record<string, boolean>>({});
  const [reportedState, setReportedState] = useState<Record<string, boolean>>({});

  const handleLike = useCallback(async (targetUserId: string, trickId: string) => {
    const key = `${targetUserId}:${trickId}`;
    if (likingMap[key]) return;
    setLikingMap(prev => ({ ...prev, [key]: true }));
    try {
      const result = await likeCko2026WarmupSubmission(targetUserId, trickId);
      setLikesCache(prev => ({ ...prev, [key]: result.likes }));
    } catch (err: any) {
      alert(err.message || '点赞失败');
    } finally {
      setLikingMap(prev => ({ ...prev, [key]: false }));
    }
  }, [likingMap]);

  const handleReport = useCallback(async (targetUserId: string, trickId: string, reason: string) => {
    const key = `${targetUserId}:${trickId}`;
    if (reportingMap[key]) return;
    setActionMessage('');
    setReportingMap(prev => ({ ...prev, [key]: true }));
    try {
      await reportCko2026WarmupSubmission(targetUserId, trickId, reason);
      // 用例：reportingMap 是「请求进行中」的互斥锁，成功后必须放回 false，
      // 否则这个 key 之后的举报会被首行的 if (reportingMap[key]) return 静默吞掉。
      setReportingMap(prev => ({ ...prev, [key]: false }));
      setReportedState(prev => ({ ...prev, [key]: true }));
      setPendingReport(null);
      setReportReasonInput('');
      setActionMessage('举报已提交，管理员将进行审核。');
    } catch (err: any) {
      setReportingMap(prev => ({ ...prev, [key]: false }));
      setActionMessage(err.message || '举报失败');
    }
  }, [reportingMap]);

  const openReportDialog = useCallback((targetUserId: string, trickId: string, trickName: string) => {
    setActionMessage('');
    setPendingReport({ userId: targetUserId, trickId, trickName });
    setReportReasonInput('');
  }, []);

  const closeReportDialog = useCallback(() => {
    setPendingReport(null);
    setReportReasonInput('');
  }, []);

  const handleUnreport = useCallback(async (targetUserId: string, trickId: string) => {
    const key = `${targetUserId}:${trickId}`;
    if (unreportingMap[key]) return;
    setActionMessage('');
    setUnreportingMap(prev => ({ ...prev, [key]: true }));
    try {
      await unreportCko2026WarmupSubmission(targetUserId, trickId);
      setReportingMap(prev => ({ ...prev, [key]: false }));
      setReportedState(prev => ({ ...prev, [key]: false }));
      setActionMessage('已取消举报。');
    } catch (err: any) {
      setActionMessage(err.message || '取消举报失败');
    } finally {
      setUnreportingMap(prev => ({ ...prev, [key]: false }));
    }
  }, [unreportingMap]);

  const handleRegister = useCallback(async () => {
    setIsRegistering(true);
    setActionMessage('');
    try {
      const updatedProfile = await registerForCko2026Warmup();
      onUpdateUser({ ...user, cko2026Warmup: updatedProfile });
      setActionMessage('报名成功，你已正式加入 CKO2026 预热赛排行榜。');
      await loadLeaderboard();
    } catch (error) {
      setActionMessage((error as Error)?.message || '报名失败');
    } finally {
      setIsRegistering(false);
    }
  }, [loadLeaderboard, onUpdateUser, user]);

  const handleUploadDisabledAttempt = useCallback(() => {
    setActionMessage(WARMUP_UPLOAD_DISABLED_MESSAGE);
  }, []);

  const prepareFileForUpload = useCallback(async (file: File, statusPrefix: string) => {
    let workingBlob: Blob = file;
    const canProcessInBrowser = canBrowserProcessVideoForUpload();
    const shouldPreferServerUpload = canUseServerSideWarmupSourceUpload(file);

    if (shouldPreferServerUpload) {
      setProcessingLabel('准备上传');
      setProcessingProgress(0);
      setActionMessage(`${statusPrefix}体积较大，正在直接上传原始视频并交由服务器自动转码...`);
      return file;
    }

    if (shouldTranscodeWarmupUploadFile(file)) {
      if (canProcessInBrowser) {
        setActionMessage(`${statusPrefix}为 MOV 视频，正在转换为网页兼容格式...`);
        setProcessingLabel('转码进度');
        setProcessingProgress(0);
        workingBlob = await transcodeVideoBlobForWebPlayback(workingBlob, (pct) => {
          setProcessingProgress(pct);
        });
      } else {
        setActionMessage(`${statusPrefix}为 MOV 视频，将直接上传并由服务器自动转为 MP4 格式。`);
      }
    }

    if (workingBlob.size > CKO2026_WARMUP_MAX_VIDEO_SIZE) {
      if (!canProcessInBrowser) {
        throw new Error(`当前浏览器不支持自动压缩，且视频超过 ${Math.round(CKO2026_WARMUP_MAX_VIDEO_SIZE / 1024 / 1024)}MB，请改用更小的视频、MP4 文件或在电脑端上传。`);
      }
      setProcessingLabel('压缩进度');
      setProcessingProgress(0);
      setActionMessage(`${statusPrefix}超过 ${Math.round(CKO2026_WARMUP_MAX_VIDEO_SIZE / 1024 / 1024)}MB，正在自动压缩...`);
      workingBlob = await compressVideoBlobToMaxSize(workingBlob, CKO2026_WARMUP_MAX_VIDEO_SIZE, (pct) => {
        setProcessingProgress(pct);
      });
    }

    if (workingBlob.size > CKO2026_WARMUP_MAX_VIDEO_SIZE) {
      throw new Error(`自动处理后仍超过 ${Math.round(CKO2026_WARMUP_MAX_VIDEO_SIZE / 1024 / 1024)}MB，请缩短视频时长后重试。`);
    }

    if (workingBlob === file || (workingBlob.size === file.size && workingBlob.type === file.type)) {
      return file;
    }

    return createWarmupUploadFile(workingBlob, file.name);
  }, []);

  const applyUpdatedWarmupProfile = useCallback((updatedProfile: ReturnType<typeof normalizeCko2026WarmupProfile>) => {
    const nextUser = { ...latestUserRef.current, cko2026Warmup: updatedProfile };
    latestUserRef.current = nextUser;
    onUpdateUser(nextUser);
  }, [onUpdateUser]);

  const uploadWarmupSubmission = useCallback(async (
    trick: TournamentTrick,
    file: File,
    options?: {
      statusPrefix?: string;
      preparingMessage?: string;
      uploadingMessage?: string;
      successMessage?: string;
    }
  ) => {
    if (!uploadsEnabledForCurrentUser) {
      throw new Error(WARMUP_UPLOAD_DISABLED_MESSAGE);
    }
    if (!profile.isRegistered) {
      throw new Error('请先点击报名，再上传对应招式视频。');
    }
    if (!isLikelyVideoFile(file)) {
      throw new Error('请选择视频文件后再上传。');
    }

    setUploadingTrickId(trick.id);
    setUploadProgress(null);
    try {
      setActionMessage(options?.preparingMessage || '正在准备视频...');
      const fileToUpload = await prepareFileForUpload(file, options?.statusPrefix || '所选视频');
      setProcessingProgress(null);
      setProcessingLabel(null);
      setUploadProgress(0);
      setActionMessage(options?.uploadingMessage || '正在上传视频...');
      const updatedProfile = await uploadCko2026WarmupVideo(trick, fileToUpload, (pct) => setUploadProgress(pct));
      applyUpdatedWarmupProfile(updatedProfile);
      setActionMessage(options?.successMessage || `已上传 ${trick.name}，正在排队处理，完成后会自动显示。`);
      return updatedProfile;
    } finally {
      setUploadingTrickId(null);
      setUploadProgress(null);
      setProcessingProgress(null);
      setProcessingLabel(null);
    }
  }, [applyUpdatedWarmupProfile, prepareFileForUpload, profile.isRegistered, uploadsEnabledForCurrentUser]);

  const handleUpload = useCallback(async (trick: TournamentTrick, file: File | null) => {
    if (!file) return;
    try {
      await uploadWarmupSubmission(trick, file);
    } catch (error) {
      setActionMessage((error as Error)?.message || '视频上传失败');
    }
  }, [uploadWarmupSubmission]);

  const deleteWarmupSubmission = useCallback(async (
    trick: TournamentTrick,
    options?: {
      successMessage?: string;
      skipLeaderboardReload?: boolean;
    }
  ) => {
    setDeletingTrickId(trick.id);
    try {
      const updatedProfile = await deleteOwnCko2026WarmupSubmission(trick.id);
      applyUpdatedWarmupProfile(updatedProfile);
      setActionMessage(options?.successMessage || `已删除 ${trick.name}，当前总分 ${getCko2026WarmupTotalScore(updatedProfile)} 分。`);
      if (!options?.skipLeaderboardReload) {
        await loadLeaderboard();
      }
      return updatedProfile;
    } finally {
      setDeletingTrickId(null);
    }
  }, [applyUpdatedWarmupProfile, loadLeaderboard]);

  const handleDeleteSubmission = useCallback((trick: TournamentTrick) => {
    setPendingDeleteTrick(trick);
  }, []);

  const confirmDeleteSubmission = useCallback(async () => {
    if (!pendingDeleteTrick) return;
    const trick = pendingDeleteTrick;

    setActionMessage('');
    try {
      await deleteWarmupSubmission(trick);
      setPendingDeleteTrick(null);
    } catch (error) {
      const rawMessage = (error as Error)?.message || '删除视频失败';
      const isLocalRouteMismatch = rawMessage === 'Non-JSON response (404)'
        && typeof window !== 'undefined'
        && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
      const deleteErrorMessage = isLocalRouteMismatch
        ? '删除接口没有响应，本地服务可能仍在旧版本。请重启本地服务后重试。'
        : rawMessage;
      setActionMessage(deleteErrorMessage);
    }
  }, [deleteWarmupSubmission, pendingDeleteTrick]);

  const handleBatchUpload = useCallback(async (items: Array<{ trick: TournamentTrick; file: File }>) => {
    if (items.length === 0 || batchOperation) return;
    setBatchOperation({ type: 'upload', current: 1, total: items.length });
    const failures: string[] = [];
    try {
      for (let index = 0; index < items.length; index += 1) {
        const item = items[index];
        setBatchOperation({ type: 'upload', current: index + 1, total: items.length });
        try {
          await uploadWarmupSubmission(item.trick, item.file, {
            statusPrefix: `第 ${index + 1}/${items.length} 个视频`,
            preparingMessage: `批量上传 ${index + 1}/${items.length}：正在准备 ${item.trick.name}...`,
            uploadingMessage: `批量上传 ${index + 1}/${items.length}：正在上传 ${item.trick.name}...`,
            successMessage: `批量上传 ${index + 1}/${items.length}：${item.trick.name} 已加入处理队列。`,
          });
        } catch (error) {
          failures.push(`${item.trick.name}：${(error as Error)?.message || '上传失败'}`);
        }
      }
      if (failures.length === 0) {
        setActionMessage(`批量上传完成，共 ${items.length} 个招式已加入处理队列。`);
      } else {
        setActionMessage(`批量上传完成，成功 ${items.length - failures.length} 个，失败 ${failures.length} 个。${failures[0]}`);
      }
    } finally {
      setBatchOperation(null);
    }
  }, [batchOperation, uploadWarmupSubmission]);

  const handleBatchDeleteRequest = useCallback((tricks: TournamentTrick[]) => {
    setPendingBatchDeleteTricks(tricks);
  }, []);

  const confirmBatchDelete = useCallback(async () => {
    if (pendingBatchDeleteTricks.length === 0 || batchOperation) return;
    setActionMessage('');
    setBatchOperation({ type: 'delete', current: 1, total: pendingBatchDeleteTricks.length });
    const failures: string[] = [];
    try {
      for (let index = 0; index < pendingBatchDeleteTricks.length; index += 1) {
        const trick = pendingBatchDeleteTricks[index];
        setBatchOperation({ type: 'delete', current: index + 1, total: pendingBatchDeleteTricks.length });
        try {
          await deleteWarmupSubmission(trick, {
            successMessage: `批量删除 ${index + 1}/${pendingBatchDeleteTricks.length}：已删除 ${trick.name}。`,
            skipLeaderboardReload: true,
          });
        } catch (error) {
          const rawMessage = (error as Error)?.message || '删除失败';
          failures.push(`${trick.name}：${rawMessage === 'Non-JSON response (404)' ? '删除接口没有响应，本地服务可能仍在旧版本。请重启本地服务后重试。' : rawMessage}`);
        }
      }
      await loadLeaderboard();
      setPendingBatchDeleteTricks([]);
      if (failures.length === 0) {
        setActionMessage(`批量删除完成，共删除 ${pendingBatchDeleteTricks.length} 个视频。`);
      } else {
        setActionMessage(`批量删除完成，成功 ${pendingBatchDeleteTricks.length - failures.length} 个，失败 ${failures.length} 个。${failures[0]}`);
      }
    } finally {
      setBatchOperation(null);
    }
  }, [batchOperation, deleteWarmupSubmission, loadLeaderboard, pendingBatchDeleteTricks]);

  // ---------- Camera Recording ----------

  const cleanupRecording = useCallback(() => {
    // 每次清理都推进一次「录制代次」：让已经排队但还没执行的
    // ondataavailable / onstop 回调全部失效（否则取消录制后，
    // onstop 仍会用只剩残片的 chunks 生成 Blob 并把预览层弹回来）。
    recordSessionRef.current += 1;
    if (countdownTimerRef.current !== null) { window.clearInterval(countdownTimerRef.current); countdownTimerRef.current = null; }
    if (recordTimerRef.current !== null) { window.clearInterval(recordTimerRef.current); recordTimerRef.current = null; }
    const recorder = mediaRecorderRef.current;
    if (recorder) {
      // 先摘掉回调再 stop()：MediaRecorder 的 onstop 是排队任务，
      // 不摘掉的话 stop() 之后它照样会执行。
      recorder.ondataavailable = null;
      recorder.onstop = null;
      if (recorder.state !== 'inactive') {
        try { recorder.stop(); } catch { /* ignore */ }
      }
    }
    mediaRecorderRef.current = null;
    if (livePreviewRef.current) {
      livePreviewRef.current.pause();
      livePreviewRef.current.srcObject = null;
    }
    if (videoPreviewRef.current) {
      videoPreviewRef.current.removeAttribute('src');
      videoPreviewRef.current.load();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    // 用 ref 读当前地址：卸载时 effect 捕获的是首次渲染的闭包，
    // 直接引用 state 会永远拿到 null，最后一段录像的 objectURL 就泄漏了。
    const currentRecordedUrl = recordedUrlRef.current;
    if (currentRecordedUrl) {
      URL.revokeObjectURL(currentRecordedUrl);
      recordedUrlRef.current = null;
    }
    chunksRef.current = [];
  }, []);

  const seekPreviewTime = useCallback((nextTime: number) => {
    const maxTime = Math.max(videoDuration, 0);
    const clampedTime = clampVideoTime(nextTime, 0, maxTime);
    setPreviewCurrentTime(clampedTime);
    const video = videoPreviewRef.current;
    if (!video) return;
    seekGuardRef.current = true;
    video.currentTime = clampedTime;
  }, [videoDuration]);

  useEffect(() => {
    const livePreview = livePreviewRef.current;
    const shouldShowLivePreview = recordState === 'requesting' || recordState === 'countdown' || recordState === 'recording';
    if (!livePreview || !shouldShowLivePreview) {
      return;
    }

    const stream = streamRef.current;
    if (!stream) {
      return;
    }

    livePreview.muted = true;
    livePreview.playsInline = true;
    if (livePreview.srcObject !== stream) {
      livePreview.srcObject = stream;
    }
    void livePreview.play().catch(() => { /* ignore autoplay rejection */ });
  }, [recordState]);

  useEffect(() => {
    if (!recordedUrl || !videoDuration) {
      setTrimThumbnails([]);
      return;
    }

    let cancelled = false;

    const buildThumbnails = async () => {
      const video = document.createElement('video');
      video.preload = 'auto';
      video.muted = true;
      video.playsInline = true;
      video.src = recordedUrl;

      await new Promise<void>((resolve, reject) => {
        video.onloadeddata = () => resolve();
        video.onerror = () => reject(new Error('timeline-load-failed'));
      });

      const canvas = document.createElement('canvas');
      canvas.width = 160;
      canvas.height = 90;
      const context = canvas.getContext('2d');
      if (!context) {
        if (!cancelled) setTrimThumbnails([]);
        return;
      }

      const captureFrame = (time: number) => new Promise<string>((resolve, reject) => {
        const handleSeeked = () => {
          try {
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/jpeg', 0.72));
          } catch {
            reject(new Error('timeline-draw-failed'));
          }
        };
        video.onseeked = handleSeeked;
        video.onerror = () => reject(new Error('timeline-seek-failed'));
        video.currentTime = clampVideoTime(time, 0, Math.max(0, videoDuration - 0.05));
      });

      const frames: string[] = [];
      for (let index = 0; index < RECORD_TRIM_FRAME_COUNT; index += 1) {
        if (cancelled) return;
        const time = (videoDuration * index) / Math.max(1, RECORD_TRIM_FRAME_COUNT - 1);
        try {
          frames.push(await captureFrame(time));
        } catch {
          if (!cancelled) setTrimThumbnails([]);
          return;
        }
      }

      if (!cancelled) setTrimThumbnails(frames);
    };

    buildThumbnails().catch(() => {
      if (!cancelled) setTrimThumbnails([]);
    });

    return () => {
      cancelled = true;
    };
  }, [recordedUrl, videoDuration]);

  useEffect(() => {
    if (recordState !== 'trimming') return;
    const clampedTime = clampVideoTime(previewCurrentTime, trimStart, trimEnd);
    if (Math.abs(clampedTime - previewCurrentTime) > 0.05) {
      seekPreviewTime(clampedTime);
    }
  }, [previewCurrentTime, recordState, seekPreviewTime, trimEnd, trimStart]);

  const requestRecordingStream = useCallback(async (deviceId?: string) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('当前浏览器不支持摄像头录制。');
    }

    const videoConstraint: MediaTrackConstraints = deviceId
      ? { deviceId: { exact: deviceId } }
      : { facingMode: 'user' };

    const constraintsList: MediaStreamConstraints[] = [
      { video: videoConstraint, audio: true },
      { video: videoConstraint, audio: false },
      { video: true, audio: false }
    ];

    let lastError: unknown = null;

    for (const constraints of constraintsList) {
      try {
        const stream = await Promise.race([
          navigator.mediaDevices.getUserMedia(constraints),
          new Promise<never>((_, reject) => {
            window.setTimeout(() => reject(new Error('camera-timeout')), 10000);
          })
        ]);
        return stream;
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError instanceof Error ? lastError : new Error('camera-unavailable');
  }, []);

  const startCameraCapture = useCallback(async (trickId: string, score: number, deviceId?: string) => {
    const requestToken = Date.now();
    captureRequestTokenRef.current = requestToken;
    cleanupRecording();
    setActionMessage('');
    setOverlayError('');
    setRecordTrickId(trickId);
    setRecordScore(score);
    setRecordState('requesting');
    setRecordTime(0);
    setRecordedBlob(null);
    setRecordedUrl(null);
    recordedUrlRef.current = null;
    setPreviewCurrentTime(0);
    setTrimThumbnails([]);
    // 重新录制时必须清掉上一段的时长与裁剪区间：新视频元数据读取失败时
    // 否则会沿用旧值，出现「裁剪区间对不上视频」的问题。
    setVideoDuration(0);
    setTrimStart(0);
    setTrimEnd(30);

    try {
      const stream = await requestRecordingStream(deviceId);
      if (captureRequestTokenRef.current !== requestToken) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
    } catch (error) {
      if (captureRequestTokenRef.current !== requestToken) return;
      const message = error instanceof Error && error.message === 'camera-timeout'
        ? '摄像头响应超时，请重新打开权限或关闭其他占用摄像头的应用后重试。'
        : '无法访问摄像头，请检查权限设置。';
      setActionMessage(message);
      setRecordState('idle');
      setRecordTrickId(null);
      setRecordScore(null);
      return;
    }

    setRecordState('countdown');
    setCountdown(3);

    // Countdown 3-2-1
    let cd = 3;
    countdownTimerRef.current = window.setInterval(() => {
      cd--;
      setCountdown(cd);
      if (cd <= 0) {
        if (countdownTimerRef.current !== null) { window.clearInterval(countdownTimerRef.current); countdownTimerRef.current = null; }
        // Start recording
        const stream = streamRef.current!;
        const recorder = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('video/webm') ? 'video/webm' : undefined });
        mediaRecorderRef.current = recorder;
        chunksRef.current = [];
        recordTimeRef.current = 0;

        const sessionId = recordSessionRef.current;
        recorder.ondataavailable = (e) => {
          if (recordSessionRef.current !== sessionId) return;
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };
        recorder.onstop = () => {
          // 已经被取消/重新开始录制的会话不再产出预览
          if (recordSessionRef.current !== sessionId) return;
          const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'video/webm' });
          const url = URL.createObjectURL(blob);
          setRecordedBlob(blob);
          setRecordedUrl(url);
          recordedUrlRef.current = url;
          setOverlayError('');
          setRecordState('preview');
          // Get duration
          const video = document.createElement('video');
          video.preload = 'metadata';
          video.src = url;
          video.onloadedmetadata = () => {
            const safeDuration = Number.isFinite(video.duration) && video.duration > 0
              ? video.duration
              : Math.max(recordTimeRef.current, RECORD_TRIM_MIN_DURATION);
            setVideoDuration(safeDuration);
            setTrimStart(0);
            setTrimEnd(safeDuration);
            setPreviewCurrentTime(0);
          };
        };

        recorder.start();
        setRecordState('recording');

        // Recording timer
        let elapsed = 0;
        recordTimerRef.current = window.setInterval(() => {
          elapsed++;
          recordTimeRef.current = elapsed;
          setRecordTime(elapsed);
          if (elapsed >= 30) {
            if (recordTimerRef.current !== null) { window.clearInterval(recordTimerRef.current); recordTimerRef.current = null; }
            const r = mediaRecorderRef.current;
            if (r && r.state === 'recording') r.stop();
          }
        }, 1000);
      }
    }, 1000);
  }, [cleanupRecording, requestRecordingStream]);

  // 枚举摄像头。浏览器只有在拿到授权后才会返回设备 label（名称），
  // 所以首次打开时先用一次临时取流换取授权、再重新枚举；
  // 授权被拒或设备没有 label 时返回现有列表，交给后续录制流程提示。
  const enumerateCameras = useCallback(async (): Promise<MediaDeviceInfo[]> => {
    if (!navigator.mediaDevices?.enumerateDevices) return [];

    const listVideoInputs = async (): Promise<MediaDeviceInfo[]> => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        return devices.filter((device) => device.kind === 'videoinput');
      } catch {
        return [];
      }
    };

    const initial = await listVideoInputs();
    if (initial.some((device) => !!device.label)) return initial;

    try {
      const stream = await requestRecordingStream();
      stream.getTracks().forEach((track) => track.stop());
    } catch {
      // 授权被拒 / 无可用摄像头：退回当前列表，让录制流程自己报错
    }
    return listVideoInputs();
  }, [requestRecordingStream]);

  const handleCameraClick = useCallback(async (trickId: string, score: number) => {
    if (!uploadsEnabledForCurrentUser) {
      setActionMessage(WARMUP_UPLOAD_DISABLED_MESSAGE);
      return;
    }
    if (recordState !== 'idle') return;
    if (cameraPickerBusyRef.current) return;
    cameraPickerBusyRef.current = true;

    try {
      const cameras = await enumerateCameras();
      if (cameras.length > 1) {
        setAvailableCameras(cameras);
        setPendingCameraTrickId(trickId);
        setPendingCameraScore(score);
        setShowCameraPicker(true);
        return;
      }
      // 0 或 1 个摄像头：直接进入录制
      const deviceId = cameras.length === 1 ? cameras[0].deviceId : undefined;
      startCameraCapture(trickId, score, deviceId);
    } finally {
      cameraPickerBusyRef.current = false;
    }
  }, [enumerateCameras, recordState, startCameraCapture, uploadsEnabledForCurrentUser]);

  const stopRecording = useCallback(() => {
    if (recordTimerRef.current !== null) { window.clearInterval(recordTimerRef.current); recordTimerRef.current = null; }
    const r = mediaRecorderRef.current;
    if (r && r.state === 'recording') r.stop();
  }, []);

  const cancelRecording = useCallback(() => {
    captureRequestTokenRef.current += 1;
    cleanupRecording();
    setRecordedBlob(null);
    setRecordedUrl(null);
    setRecordState('idle');
    setRecordTrickId(null);
    setRecordScore(null);
    setRecordTime(0);
    recordTimeRef.current = 0;
    setTrimStart(0);
    setTrimEnd(30);
    setVideoDuration(0);
    setPreviewCurrentTime(0);
    setTrimThumbnails([]);
    setOverlayError('');
  }, [cleanupRecording]);

  const openTrimming = useCallback(() => {
    videoPreviewRef.current?.pause();
    seekPreviewTime(trimStart);
    setRecordState('trimming');
  }, [seekPreviewTime, trimStart]);

  const handlePreviewTimeUpdate = useCallback(() => {
    const video = videoPreviewRef.current;
    if (!video) return;

    // Skip timeupdate triggered by our own programmatic seek
    if (seekGuardRef.current) {
      seekGuardRef.current = false;
      setPreviewCurrentTime(video.currentTime);
      return;
    }

    // Only loop within trim range during actual playback — don't fight user scrubbing
    if (recordState === 'trimming' && !video.paused && video.currentTime >= trimEnd) {
      seekGuardRef.current = true;
      video.currentTime = trimStart;
      setPreviewCurrentTime(trimStart);
      return;
    }

    setPreviewCurrentTime(video.currentTime);
  }, [recordState, trimEnd, trimStart]);

  const handleVideoPlayInTrim = useCallback(() => {
    const video = videoPreviewRef.current;
    if (!video || recordState !== 'trimming') return;
    if (video.currentTime < trimStart || video.currentTime > trimEnd) {
      seekGuardRef.current = true;
      video.currentTime = trimStart;
      setPreviewCurrentTime(trimStart);
    }
  }, [recordState, trimEnd, trimStart]);

  const playTrimPreview = useCallback(() => {
    const video = videoPreviewRef.current;
    if (!video) return;
    if (video.currentTime < trimStart || video.currentTime > trimEnd) {
      seekGuardRef.current = true;
      video.currentTime = trimStart;
      setPreviewCurrentTime(trimStart);
    }
    void video.play().catch(() => { /* ignore autoplay rejection */ });
  }, [trimEnd, trimStart]);

  const requestRecordedVideoSubmit = useCallback(() => {
    if (!recordedBlob || !recordTrickId || recordedBlob.size === 0) {
      setOverlayError('录制数据无效，请重新录制。');
      return;
    }
    setOverlayError('');
    videoPreviewRef.current?.pause();
    setRecordState('confirm');
  }, [recordTrickId, recordedBlob, trimEnd, trimStart, videoDuration]);

  const hasTrimSelection = useMemo(() => (
    trimStart > 0.05 || trimEnd < Math.max(0, videoDuration - 0.05)
  ), [trimEnd, trimStart, videoDuration]);

  const submitRecordedVideo = useCallback(async () => {
    if (!recordedBlob || !recordTrickId || recordedBlob.size === 0) {
      setOverlayError('录制数据无效，请重新录制。');
      return;
    }

    if (!uploadsEnabledForCurrentUser) {
      setOverlayError(WARMUP_UPLOAD_DISABLED_MESSAGE);
      return;
    }

    const trick = CKO2026_TRICKS.find(t => t.id === recordTrickId);
    if (!trick) {
      setOverlayError('未找到对应招式，请重试。');
      return;
    }

    setRecordState('submitting');
    setOverlayError('');
    setUploadingTrickId(recordTrickId);
    setUploadProgress(null);
    try {
      const processedBlob = hasTrimSelection
        ? await exportTrimmedVideoBlob(recordedBlob, trimStart, trimEnd)
        : recordedBlob;
      let blobToUpload = processedBlob;
      if (blobToUpload.size > CKO2026_WARMUP_MAX_VIDEO_SIZE) {
        setProcessingLabel('压缩进度');
        setProcessingProgress(0);
        setActionMessage(`录制视频超过 ${Math.round(CKO2026_WARMUP_MAX_VIDEO_SIZE / 1024 / 1024)}MB，正在自动压缩...`);
        blobToUpload = await compressVideoBlobToMaxSize(blobToUpload, CKO2026_WARMUP_MAX_VIDEO_SIZE, (pct) => {
          setProcessingProgress(pct);
        });
        if (blobToUpload.size > CKO2026_WARMUP_MAX_VIDEO_SIZE) {
          throw new Error(`自动压缩后仍超过 ${Math.round(CKO2026_WARMUP_MAX_VIDEO_SIZE / 1024 / 1024)}MB，请缩短录制时长后重试。`);
        }
      }
      const file = createWarmupUploadFile(blobToUpload, `recorded-trick-${Date.now()}.webm`);

      setProcessingProgress(null);
      setProcessingLabel(null);
      setUploadProgress(0);
      setActionMessage('正在上传视频...');
      const updatedProfile = await uploadCko2026WarmupVideo(trick, file, (pct) => setUploadProgress(pct));
      onUpdateUser({ ...user, cko2026Warmup: updatedProfile });
      setActionMessage(`已上传 ${trick.name}（录制视频），正在排队处理，完成后会自动更新总分和排行榜。`);
      // Only clean up on success
      cancelRecording();
      setRecordState('idle');
      setUploadingTrickId(null);
      setUploadProgress(null);
      setProcessingProgress(null);
      setProcessingLabel(null);
    } catch (error) {
      setOverlayError((error as Error)?.message || '视频上传失败');
      // Stay on preview so user can retry without re-recording
      setRecordState(hasTrimSelection ? 'trimming' : 'preview');
      setUploadingTrickId(null);
      setUploadProgress(null);
      setProcessingProgress(null);
      setProcessingLabel(null);
    }
  }, [recordedBlob, recordTrickId, onUpdateUser, user, loadLeaderboard, cancelRecording, hasTrimSelection, trimStart, trimEnd, uploadsEnabledForCurrentUser]);

  // Cleanup recording on unmount
  useEffect(() => {
    return () => cleanupRecording();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedLeaderboardSummaryEntry = useMemo(() => {
    if (!isPlayerDetailPage) return null;
    const selectedUserId = params.userId ? decodeURIComponent(params.userId) : '';
    return leaderboard.find((entry) => entry.userId === selectedUserId) ?? null;
  }, [isPlayerDetailPage, leaderboard, params.userId]);

  useEffect(() => {
    if (!isPlayerDetailPage) {
      setSelectedLeaderboardDetail(null);
      setIsSelectedLeaderboardLoading(false);
      setSelectedLeaderboardError('');
      return;
    }

    const selectedUserId = params.userId ? decodeURIComponent(params.userId) : '';
    if (!selectedUserId) {
      setSelectedLeaderboardDetail(null);
      setSelectedLeaderboardError('');
      return;
    }

    let cancelled = false;
    setIsSelectedLeaderboardLoading(true);
    setSelectedLeaderboardError('');
    setSelectedLeaderboardDetail(null);
    // 切换选手时关掉「N 级招式」弹层：组件在 /wireless-progress/player/:userId
    // 之间跳转时不会卸载，不重置的话旧弹层会盖在新选手页面上。
    setSelectedVideoScore(null);

    fetchCko2026WarmupLeaderboardEntry(selectedUserId)
      .then((entry) => {
        if (cancelled) return;
        setSelectedLeaderboardDetail(entry);
      })
      .catch((error) => {
        if (cancelled) return;
        setSelectedLeaderboardError((error as Error)?.message || '选手详情加载失败');
      })
      .finally(() => {
        if (cancelled) return;
        setIsSelectedLeaderboardLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isPlayerDetailPage, params.userId]);

  const selectedLeaderboardEntry = useMemo(() => {
    if (!isPlayerDetailPage) return null;
    const selectedUserId = params.userId ? decodeURIComponent(params.userId) : '';
    if (!selectedUserId) return null;
    return selectedLeaderboardDetail && selectedLeaderboardDetail.userId === selectedUserId
      ? selectedLeaderboardDetail
      : null;
  }, [isPlayerDetailPage, params.userId, selectedLeaderboardDetail]);

  const selectedLeaderboardRank = useMemo(() => {
    const selectedUserId = params.userId ? decodeURIComponent(params.userId) : '';
    if (!selectedUserId) return null;
    const rank = leaderboard.findIndex((entry) => entry.userId === selectedUserId);
    return rank >= 0 ? rank + 1 : null;
  }, [leaderboard, params.userId]);

  const shouldReduceMobileVisualEffects = useMemo(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    const hostname = window.location.hostname;
    // 原生 App 的页面也是 localhost，不能当成本地预览，否则真机降级失效。
    const isPreviewHost = !isNativeApp() && (hostname === 'localhost' || hostname === '127.0.0.1');
    if (isPreviewHost) return false;
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const userAgent = navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
    return prefersReducedMotion || isIOS;
  }, []);

  if (isMyTricksPage) {
    return (
      <div ref={scrollContainerRef} className="h-full w-full overflow-y-auto overflow-x-hidden custom-scrollbar px-3 pt-3 pb-20 md:px-5 md:pb-5 xl:py-4">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate('/wireless-progress')}
              className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-zinc-200 transition-colors hover:border-white/20 hover:bg-white/10"
            >
              <ArrowLeft size={16} />
              返回预热赛首页
            </button>
          </div>

          <section
            className="rounded-[24px] border border-cyan-400/20 bg-[linear-gradient(135deg,rgba(6,182,212,0.16),rgba(14,14,14,0.92),rgba(37,99,235,0.12))] transition-all duration-300"
          >
            {!isTricksSectionExpanded ? (
              <button
                type="button"
                onClick={() => setIsTricksSectionExpanded(true)}
                className="w-full flex items-center justify-between gap-3 p-4 md:p-5 text-left"
              >
                <div className="flex items-center gap-2">
                  <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/25 bg-cyan-500/10 px-2.5 py-1 text-[11px] font-orbitron uppercase tracking-[0.18em] text-cyan-200 shrink-0">
                    <Upload size={14} />
                    My Tricks
                    {activeRemovalNotices.length > 0 && (
                      <span className="ml-1 rounded-full bg-red-500 px-1.5 py-px text-[9px] font-bold text-white leading-none">
                        {activeRemovalNotices.length}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-zinc-500">我的招式上传</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-[10px] text-zinc-500">完成</div>
                      <div className="text-base font-orbitron text-cyan-300">{completedCount}</div>
                    </div>
                    <div className="h-6 w-px bg-white/10" />
                    <div className="text-right">
                      <div className="text-[10px] text-zinc-500">总分</div>
                      <div className="text-base font-orbitron text-emerald-300">{totalScore}</div>
                    </div>
                  </div>
                  <ChevronDown size={16} className="text-zinc-500" />
                </div>
              </button>
            ) : (
              <>
                <div className="flex items-start justify-between gap-3 p-4 md:p-5 pb-0">
                  <div className="flex-1">
                    <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/25 bg-cyan-500/10 px-2.5 py-1 text-[11px] font-orbitron uppercase tracking-[0.18em] text-cyan-200">
                      <Upload size={14} />
                      My Tricks
                      {activeRemovalNotices.length > 0 && (
                        <span className="ml-1 rounded-full bg-red-500 px-1.5 py-px text-[9px] font-bold text-white leading-none">
                          {activeRemovalNotices.length}
                        </span>
                      )}
                    </div>
                    <h1 className="mt-2.5 text-2xl font-orbitron font-black tracking-[0.12em] text-white md:text-3xl">我的招式上传</h1>
                    <p className="mt-2 max-w-3xl text-sm leading-6 text-zinc-300">
                      这里集中处理你在 CKO2026 预热赛中的全部招式上传。按等级展开后即可为对应招式上传视频，上传后会进入处理队列，处理完成后自动更新你的总分和排行榜。
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsTricksSectionExpanded(false)}
                    className="shrink-0 mt-1 rounded-lg p-1.5 text-zinc-500 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="p-4 md:p-5 pt-4">
                  <div className="grid grid-cols-2 gap-2.5 md:min-w-[320px]">
                    <div className="rounded-2xl border border-white/10 bg-black/25 px-3 py-2.5">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">我的完成</div>
                      <div className="mt-1.5 text-xl font-orbitron text-cyan-300">{completedCount}</div>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-black/25 px-3 py-2.5">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">我的总分</div>
                      <div className="mt-1.5 text-xl font-orbitron text-emerald-300">{totalScore}</div>
                    </div>
                  </div>

                  {actionMessage && (
                    <div className="mt-3 rounded-2xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" />
                            <span className="text-xs font-semibold text-cyan-200">操作通知</span>
                          </div>
                          <div className="text-sm text-cyan-100 whitespace-pre-line">{actionMessage}</div>
                          {processingProgress != null && (
                            <div className="mt-2">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs text-cyan-200/70">{processingLabel || '处理进度'}</span>
                                <span className="text-xs font-mono text-cyan-300">{processingProgress}%</span>
                              </div>
                              <div className="h-1.5 w-full rounded-full bg-cyan-500/20 overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-cyan-400 transition-all duration-300"
                                  style={{ width: `${processingProgress}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setActionMessage('')}
                          className="shrink-0 rounded-lg p-1 text-cyan-400/50 hover:text-white hover:bg-white/10 transition-colors"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  )}

                  {activeRemovalNotices.length > 0 && (
                    <div className="mt-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle size={14} className="text-zinc-300 shrink-0" />
                        <span className="text-xs font-semibold text-zinc-100">招式通知</span>
                      </div>
                      <div className="flex flex-col gap-2">
                        {activeRemovalNotices.map((notice, idx) => {
                          const noticeStyle = getWarmupNoticeStyle(notice);
                          return (
                            <div key={idx} className={`rounded-lg border px-3 py-2 ${noticeStyle.card}`}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] ${noticeStyle.wrapper} ${noticeStyle.title}`}>
                                    {getWarmupNoticeType(notice) === 'prompt' ? '提示' : '移除'}
                                  </div>
                                  <div className="mt-1.5 text-xs text-zinc-200">
                                    <span className="font-semibold">{notice.trickName}</span>
                                    <span className="text-zinc-500 ml-1">({notice.score}分)</span>
                                  </div>
                                  <div className={`mt-1 text-[11px] leading-relaxed ${noticeStyle.icon}`}>
                                    {noticeStyle.label}{notice.reason}
                                  </div>
                                  <div className="mt-1 text-[10px] text-zinc-500">
                                    {new Date(notice.removedAt).toLocaleString('zh-CN')}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => dismissRemovalNotice(notice.trickId, notice.removedAt)}
                                  className="shrink-0 rounded p-0.5 text-zinc-500 hover:text-white hover:bg-white/10"
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </section>

          <WarmupUploadPanel
            profile={profile}
            submissionMap={submissionMap}
            canUseBatchActions={canUseBatchActions}
            uploadsEnabled={uploadsEnabledForCurrentUser}
            uploadingTrickId={uploadingTrickId}
            deletingTrickId={deletingTrickId}
            selectedUploadScore={selectedUploadScore}
            onSelectScore={setSelectedUploadScore}
            onUpload={handleUpload}
            onUploadDisabledAttempt={handleUploadDisabledAttempt}
            onDelete={handleDeleteSubmission}
            onStartCamera={handleCameraClick}
            isRecording={recordState !== 'idle'}
            uploadProgress={uploadProgress}
            processingProgress={processingProgress}
            processingLabel={processingLabel}
            batchOperation={batchOperation}
            onBatchUpload={handleBatchUpload}
            onBatchDelete={handleBatchDeleteRequest}
            onSetActionMessage={setActionMessage}
          />

          {pendingDeleteTrick && (
            <div className="fixed inset-0 z-[160] flex items-center justify-center bg-black/70 p-4" onClick={() => !deletingTrickId && setPendingDeleteTrick(null)}>
              <div
                className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-950 p-5 shadow-2xl"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 rounded-xl border border-red-400/20 bg-red-500/10 p-2 text-red-200">
                    <Trash2 size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-base font-semibold text-white">确认删除视频</div>
                    <div className="mt-2 whitespace-pre-line text-sm leading-6 text-zinc-300">{pendingDeleteTrick.name}</div>
                    <div className="mt-2 text-xs leading-5 text-zinc-500">删除后该招式的视频和对应分数都会取消。</div>
                  </div>
                </div>

                {actionMessage && (
                  <div className="mt-4 rounded-xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm leading-6 text-red-100">
                    {actionMessage}
                  </div>
                )}

                <div className="mt-5 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setPendingDeleteTrick(null)}
                    disabled={!!deletingTrickId}
                    className="flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-zinc-200 transition-colors hover:bg-white/10 disabled:opacity-50"
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    onClick={() => { void confirmDeleteSubmission(); }}
                    disabled={!!deletingTrickId}
                    className="flex-1 rounded-xl border border-red-400/20 bg-red-500/15 px-4 py-3 text-sm font-semibold text-red-100 transition-colors hover:bg-red-500/25 disabled:opacity-50"
                  >
                    {deletingTrickId ? '删除中...' : '确认删除'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {pendingBatchDeleteTricks.length > 0 && (
            <div className="fixed inset-0 z-[160] flex items-center justify-center bg-black/70 p-4" onClick={() => !deletingTrickId && !batchOperation && setPendingBatchDeleteTricks([])}>
              <div
                className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-950 p-5 shadow-2xl"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 rounded-xl border border-red-400/20 bg-red-500/10 p-2 text-red-200">
                    <Trash2 size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-base font-semibold text-white">确认批量删除视频</div>
                    <div className="mt-2 text-sm leading-6 text-zinc-300">
                      将按顺序删除 {pendingBatchDeleteTricks.length} 个已上传招式视频。
                    </div>
                    <div className="mt-2 max-h-40 overflow-y-auto rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-xs leading-5 text-zinc-400">
                      {pendingBatchDeleteTricks.map((trick) => trick.name).join('、')}
                    </div>
                    <div className="mt-2 text-xs leading-5 text-zinc-500">删除后对应招式的视频和分数都会取消。</div>
                  </div>
                </div>

                {actionMessage && (
                  <div className="mt-4 rounded-xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm leading-6 text-red-100">
                    {actionMessage}
                  </div>
                )}

                <div className="mt-5 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setPendingBatchDeleteTricks([])}
                    disabled={!!batchOperation}
                    className="flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-zinc-200 transition-colors hover:bg-white/10 disabled:opacity-50"
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    onClick={() => { void confirmBatchDelete(); }}
                    disabled={!!batchOperation}
                    className="flex-1 rounded-xl border border-red-400/20 bg-red-500/15 px-4 py-3 text-sm font-semibold text-red-100 transition-colors hover:bg-red-500/25 disabled:opacity-50"
                  >
                    {batchOperation?.type === 'delete'
                      ? `删除中 ${batchOperation.current}/${batchOperation.total}`
                      : '确认批量删除'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Camera Picker */}
          {showCameraPicker && (
            <div className="fixed inset-0 z-[170] flex items-center justify-center bg-black/70 p-4" onClick={() => setShowCameraPicker(false)}>
              <div
                className="w-full max-w-sm rounded-2xl border border-white/10 bg-zinc-950 p-5 shadow-2xl"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="rounded-xl border border-emerald-400/20 bg-emerald-500/10 p-2 text-emerald-200">
                    <Camera size={18} />
                  </div>
                  <div>
                    <div className="text-base font-semibold text-white">选择摄像头</div>
                    <div className="text-xs text-zinc-500">检测到多个摄像头</div>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  {availableCameras.map((camera) => (
                    <button
                      key={camera.deviceId}
                      type="button"
                      onClick={() => {
                        setShowCameraPicker(false);
                        startCameraCapture(pendingCameraTrickId!, pendingCameraScore!, camera.deviceId);
                      }}
                      className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-left text-sm text-zinc-200 transition-colors hover:bg-white/10 hover:border-emerald-400/30"
                    >
                      <Camera size={16} className="text-emerald-300 shrink-0" />
                      <span className="min-w-0 truncate">{camera.label || `摄像头 ${availableCameras.indexOf(camera) + 1}`}</span>
                    </button>
                  ))}
                </div>

                <div className="mt-4 flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCameraPicker(false);
                      startCameraCapture(pendingCameraTrickId!, pendingCameraScore!);
                    }}
                    className="flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-zinc-200 transition-colors hover:bg-white/10"
                  >
                    使用默认
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCameraPicker(false)}
                    className="flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-zinc-200 transition-colors hover:bg-white/10"
                  >
                    取消
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Recording Overlay */}
          {recordState !== 'idle' && (
            <div className="fixed inset-0 z-[200] overflow-y-auto bg-black/30 px-3 py-6 backdrop-blur-xl sm:px-4 sm:py-8">
              <div className="flex min-h-[100svh] items-center justify-center py-[max(4.5rem,8svh)]">
                <div className="mx-auto w-full max-w-2xl max-h-[calc(100svh-6.5rem)] overflow-y-auto rounded-[28px] border border-white/12 bg-zinc-900/88 p-4 shadow-[0_24px_80px_rgba(0,0,0,0.5)] backdrop-blur-md sm:max-h-[calc(100svh-7rem)] sm:p-5 md:p-6">

                {/* Requesting camera permission */}
                {recordState === 'requesting' && (
                  <div className="flex flex-col items-center gap-6 py-8">
                    <Camera size={32} className="text-cyan-400 animate-pulse" />
                    <div className="text-sm text-zinc-300">正在请求摄像头权限...</div>
                    <div className="text-center text-xs leading-6 text-zinc-500">
                      请在浏览器弹窗中允许摄像头访问，成功后将自动开始倒计时。
                    </div>
                  </div>
                )}

                {/* Countdown */}
                {recordState === 'countdown' && (
                  <div className="flex flex-col items-center gap-6 py-8">
                    <Camera size={32} className="text-emerald-400" />
                    <div className="text-sm text-zinc-400">准备录制...</div>
                    <div className="text-8xl font-orbitron font-black text-emerald-400 animate-pulse">
                      {countdown}
                    </div>
                    <div className="text-xs text-zinc-500">请面向摄像头</div>
                  </div>
                )}

                {/* Recording */}
                {recordState === 'recording' && (
                  <div className="flex flex-col gap-3 sm:gap-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full bg-red-500 animate-pulse" />
                        <span className="text-sm font-orbitron text-red-400 tabular-nums">
                          {String(Math.floor(recordTime / 60)).padStart(2, '0')}:{String(recordTime % 60).padStart(2, '0')}
                        </span>
                        <span className="text-xs text-zinc-500">/ 00:30</span>
                      </div>
                      <div className="text-[10px] font-orbitron text-zinc-600">REC</div>
                    </div>
                    {/* Progress bar */}
                    <div className="h-1 overflow-hidden rounded-full bg-white/[0.06]">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-red-500 to-emerald-400 transition-all duration-1000"
                        style={{ width: `${(recordTime / 30) * 100}%` }}
                      />
                    </div>
                    <div className="overflow-hidden rounded-2xl border border-white/10 bg-black aspect-[4/3] sm:aspect-video">
                      <video
                        ref={livePreviewRef}
                        className="h-full w-full bg-black object-cover"
                        autoPlay
                        muted
                        playsInline
                      />
                    </div>
                    <button
                      type="button"
                      onClick={stopRecording}
                      className="w-full rounded-xl border border-red-400/30 bg-red-500/15 px-4 py-3 text-sm font-semibold text-red-200 transition-colors hover:bg-red-500/25"
                    >
                      停止录制
                    </button>
                  </div>
                )}

                {/* Preview */}
                {(recordState === 'preview' || recordState === 'trimming' || recordState === 'confirm' || recordState === 'submitting') && (
                  <div className="flex flex-col gap-3 sm:gap-4">
                    <div className="flex items-start justify-between gap-3 sm:items-center">
                      <span className="text-sm font-semibold text-white">
                        {recordState === 'trimming'
                          ? '视频裁切'
                          : recordState === 'confirm'
                            ? '确认提交'
                            : recordState === 'submitting'
                              ? '正在上传'
                              : '视频预览'}
                      </span>
                      <span className="text-xs text-zinc-500">
                        {displayVideoDuration.toFixed(1)}秒
                      </span>
                    </div>

                    {/* Video preview */}
                    <div className="overflow-hidden rounded-2xl border border-white/10 bg-black aspect-[4/3] sm:aspect-video">
                      {recordedUrl && (
                        <video
                          ref={videoPreviewRef}
                          src={recordedUrl}
                          className="h-full w-full object-contain"
                          controls
                          playsInline
                          onLoadedMetadata={() => {
                            setPreviewCurrentTime(videoPreviewRef.current?.currentTime ?? 0);
                          }}
                          onTimeUpdate={handlePreviewTimeUpdate}
                          onPlay={handleVideoPlayInTrim}
                        />
                      )}
                    </div>

                    {/* Trim controls */}
                    {recordState === 'trimming' && (
                      <VideoTrimTimeline
                        duration={displayVideoDuration}
                        trimStart={trimStart}
                        trimEnd={trimEnd}
                        currentTime={previewCurrentTime}
                        thumbnails={trimThumbnails}
                        onTrimStartChange={setTrimStart}
                        onTrimEndChange={setTrimEnd}
                        onSeek={seekPreviewTime}
                      />
                    )}

                    {/* Action buttons */}
                    <div className="flex flex-col gap-2 sm:gap-2.5">
                      {overlayError && (
                        <div className="rounded-xl border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
                          {overlayError}
                        </div>
                      )}
                      {recordState === 'preview' && (
                        <>
                          <button
                            type="button"
                            onClick={requestRecordedVideoSubmit}
                            className="w-full rounded-xl border border-emerald-400/30 bg-emerald-500/15 px-4 py-3 text-sm font-semibold text-emerald-200 transition-colors hover:bg-emerald-500/25"
                          >
                            直接提交
                          </button>
                          <button
                            type="button"
                            onClick={openTrimming}
                            className="w-full rounded-xl border border-cyan-400/20 bg-cyan-500/10 px-4 py-3 text-sm text-cyan-100 transition-colors hover:bg-cyan-500/20"
                          >
                            裁切视频
                          </button>
                        </>
                      )}
                      {recordState === 'trimming' && (
                        <button
                          type="button"
                          onClick={requestRecordedVideoSubmit}
                          className="w-full rounded-xl border border-cyan-400/30 bg-cyan-500/15 px-4 py-3 text-sm font-semibold text-cyan-200 transition-colors hover:bg-cyan-500/25"
                        >
                          提交裁切后的视频
                        </button>
                      )}
                      {recordState === 'confirm' && (
                        <div className="rounded-xl border border-yellow-400/20 bg-yellow-500/10 px-4 py-3">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="h-2 w-2 rounded-full bg-yellow-400 animate-pulse" />
                            <span className="text-sm font-semibold text-yellow-200">确认提交</span>
                          </div>
                          <p className="text-xs text-yellow-100/70 mb-3">
                            提交后该视频将作为此招式的正式录像，可在选手详情页查看。
                          </p>
                          <div className="flex flex-col gap-2 sm:flex-row">
                            <button
                              type="button"
                              onClick={submitRecordedVideo}
                              className="flex-1 rounded-lg border border-emerald-400/30 bg-emerald-500/15 px-3 py-2.5 text-xs font-semibold text-emerald-200 hover:bg-emerald-500/25"
                            >
                              确认提交
                            </button>
                            <button
                              type="button"
                              onClick={() => setRecordState(hasTrimSelection ? 'trimming' : 'preview')}
                              className="flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-xs text-zinc-300 hover:bg-white/10"
                            >
                              返回
                            </button>
                          </div>
                        </div>
                      )}
                      {recordState === 'submitting' && (
                        <div className="rounded-xl border border-cyan-400/15 bg-cyan-500/10 px-4 py-3 text-sm text-cyan-100">
                          正在上传录制视频，请稍候...
                        </div>
                      )}
                      {recordState !== 'confirm' && recordState !== 'submitting' && (
                        <button
                          type="button"
                          onClick={cancelRecording}
                          className="w-full rounded-xl border border-white/[0.04] px-4 py-3 text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
                        >
                          取消
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Cancel during countdown/recording */}
                {(recordState === 'requesting' || recordState === 'countdown' || recordState === 'recording') && (
                  <button
                    type="button"
                    onClick={cancelRecording}
                    className="mt-4 w-full rounded-xl border border-white/[0.04] px-4 py-3 text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
                  >
                    {recordState === 'requesting' ? '取消请求' : '取消录制'}
                  </button>
                )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isPlayerDetailPage) {
    return (
      <div className="h-full w-full overflow-y-auto overflow-x-hidden custom-scrollbar px-3 py-3 md:px-5 md:py-4">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">

          {/* Back button */}
          <button
            type="button"
            onClick={() => navigate('/wireless-progress')}
            className="self-end inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-zinc-200 transition-colors hover:border-white/20 hover:bg-white/10"
          >
            <ArrowLeft size={16} />
            返回排行榜
          </button>

          {!selectedLeaderboardEntry ? (
            <div className="rounded-[24px] border border-white/10 bg-zinc-950/70 p-5 text-center text-zinc-400">
              {isSelectedLeaderboardLoading
                ? '选手详情加载中...'
                : selectedLeaderboardError
                  ? selectedLeaderboardError
                  : '未找到该选手的预热赛记录。'}
            </div>
          ) : (
            <>
              <section className="rounded-[24px] border border-yellow-400/20 bg-[linear-gradient(135deg,rgba(250,204,21,0.14),rgba(14,14,14,0.92),rgba(16,185,129,0.10))] p-4 md:p-5">
                <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_360px]">
                  <div className="flex items-center gap-3">
                    <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-3xl border border-white/10 bg-white/5 text-lg font-orbitron text-white">
                      {selectedLeaderboardEntry.avatarUrl ? (
                        <img src={selectedLeaderboardEntry.avatarUrl} alt={`${selectedLeaderboardEntry.displayName} 头像`} className="h-full w-full object-cover" />
                      ) : (
                        getAvatarInitial(selectedLeaderboardEntry.displayName)
                      )}
                    </div>

                    <div>
                      <div className="inline-flex rounded-full border border-yellow-400/20 bg-yellow-500/10 px-2.5 py-1 text-[11px] font-orbitron uppercase tracking-[0.16em] text-yellow-100">
                        Rank #{selectedLeaderboardRank ?? '-'}
                      </div>
                      <h1 className="mt-2 text-2xl font-orbitron font-black tracking-[0.12em] text-white md:text-3xl">
                        {selectedLeaderboardEntry.displayName}
                      </h1>
                      <p className="mt-1.5 text-sm text-zinc-400">
                        报名时间：{formatTime(selectedLeaderboardEntry.registeredAt)}，最近更新：{formatTime(selectedLeaderboardEntry.updatedAt)}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2.5">
                    <div className="rounded-2xl border border-white/10 bg-black/25 px-3 py-2.5">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">排名</div>
                      <div className="mt-1.5 text-xl font-orbitron text-yellow-200">{selectedLeaderboardRank ?? '-'}</div>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-black/25 px-3 py-2.5">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">总分</div>
                      <div className="mt-1.5 text-xl font-orbitron text-emerald-300">{selectedLeaderboardEntry.totalScore}</div>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-black/25 px-3 py-2.5">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">完成</div>
                      <div className="mt-1.5 text-xl font-orbitron text-cyan-300">{selectedLeaderboardEntry.completedCount}</div>
                    </div>
                  </div>
                </div>
              </section>

              <section className="rounded-[24px] border border-white/10 bg-zinc-950/70 p-3 md:p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-orbitron font-bold tracking-[0.12em] text-white">招式视频</h2>
                    <p className="mt-1 text-xs text-zinc-500">点击等级查看该选手已完成的招式视频。</p>
                  </div>
                  <div className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-2.5 py-1 text-[11px] font-orbitron uppercase tracking-[0.16em] text-cyan-100">
                    Videos
                  </div>
                </div>

                {selectedLeaderboardEntry.submissions.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-white/10 px-4 py-8 text-center text-sm text-zinc-500">
                    该选手暂未上传任何招式视频。
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-1.5 min-[400px]:grid-cols-4 sm:grid-cols-5 sm:gap-2">
                    {trickGroups.map((group) => {
                      const completedCount = selectedLeaderboardEntry.submissions.filter(
                        (sub) => sub.score === group.score
                      ).length;
                      const progressPct = Math.round((completedCount / group.tricks.length) * 100);
                      return (
                        <button
                          key={group.score}
                          type="button"
                          onClick={() => completedCount > 0 && setSelectedVideoScore(group.score)}
                          disabled={completedCount === 0}
                          className={`flex flex-col items-center justify-center gap-1 rounded-2xl border px-2 py-3 text-center transition-colors ${
                            completedCount > 0
                              ? 'border-white/10 bg-zinc-900 hover:border-cyan-400/30 hover:bg-zinc-800'
                              : 'border-white/[0.04] bg-zinc-900/40 opacity-35'
                          }`}
                        >
                          <div className={`text-lg font-orbitron font-bold ${completedCount > 0 ? 'text-white' : 'text-zinc-600'}`}>{group.score}</div>
                          <div className="text-[9px] uppercase tracking-[0.12em] text-zinc-500">级</div>
                          <div className="mt-0.5 h-1 w-full overflow-hidden rounded-full bg-white/[0.06]">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-cyan-500/60 to-cyan-400/60 transition-all duration-300"
                              style={{ width: `${progressPct}%` }}
                            />
                          </div>
                          <div className="text-[10px] text-zinc-500">{completedCount}/{group.tricks.length}</div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </section>

              {selectedVideoScore !== null && (() => {
                const scoreSubmissions = selectedLeaderboardEntry.submissions.filter(
                  (sub) => sub.score === selectedVideoScore
                );
                return (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setSelectedVideoScore(null)}>
                    <div
                      className="w-full max-w-2xl max-h-[85vh] overflow-y-auto overflow-x-hidden rounded-2xl border border-zinc-800 bg-zinc-950 p-4 shadow-2xl"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div className="flex items-center gap-2">
                          <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-yellow-400/20 bg-yellow-500/10 text-sm font-orbitron text-yellow-200">
                            {selectedVideoScore}级
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-white">{selectedVideoScore} 级招式</div>
                            <div className="text-[11px] text-zinc-500">{scoreSubmissions.length} 个视频</div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSelectedVideoScore(null)}
                          className="fixed z-50 top-20 xl:top-24 right-4 inline-flex items-center justify-center h-10 w-10 rounded-2xl border-2 border-zinc-600 bg-zinc-800/90 text-white shadow-lg backdrop-blur-sm transition-all hover:bg-zinc-700/90 hover:border-zinc-400 active:scale-95"
                        >
                          <X size={18} />
                        </button>
                      </div>

                      <div className="grid gap-2.5">
                        {scoreSubmissions.map((submission) => (
                          <div key={submission.trickId} className="rounded-xl border border-white/10 bg-zinc-900 p-2.5">
                            <div className="flex items-start justify-between gap-2.5">
                              <div>
                                <div className="text-sm font-semibold whitespace-pre-line leading-5 text-white">{submission.trickName}</div>
                                <div className="mt-1 text-xs text-zinc-500">{formatTime(submission.uploadedAt)}</div>
                              </div>
                              <div className="rounded-lg border border-yellow-400/20 bg-yellow-500/10 px-2 py-0.5 text-xs font-orbitron text-yellow-100">
                                {submission.score}分
                              </div>
                            </div>
                            <video
                              className="mt-2.5 aspect-video w-full rounded-xl border border-white/10 bg-black"
                              src={submission.videoUrl}
                              controls
                              preload="metadata"
                            />
                            <div className="mt-2.5 flex items-center justify-between gap-2">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const isReported = reportedState[`${selectedLeaderboardEntry.userId}:${submission.trickId}`] ?? submission.reported;
                                  if (isReported) {
                                    handleUnreport(selectedLeaderboardEntry.userId, submission.trickId);
                                  } else {
                                    openReportDialog(selectedLeaderboardEntry.userId, submission.trickId, submission.trickName);
                                  }
                                }}
                                disabled={
                                  ((reportedState[`${selectedLeaderboardEntry.userId}:${submission.trickId}`] ?? submission.reported)
                                    ? unreportingMap[`${selectedLeaderboardEntry.userId}:${submission.trickId}`]
                                    : reportingMap[`${selectedLeaderboardEntry.userId}:${submission.trickId}`])
                                }
                                className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs transition-colors disabled:opacity-50 disabled:text-zinc-600 ${
                                  (reportedState[`${selectedLeaderboardEntry.userId}:${submission.trickId}`] ?? submission.reported)
                                    ? 'border-green-400/20 bg-green-500/10 text-green-200 hover:border-green-300/40 hover:bg-green-500/20'
                                    : 'border-orange-400/20 bg-orange-500/10 text-orange-200 hover:border-orange-300/40 hover:bg-orange-500/20'
                                }`}
                              >
                                <Flag size={13} />
                                <span>{(reportedState[`${selectedLeaderboardEntry.userId}:${submission.trickId}`] ?? submission.reported) ? '取消举报' : '举报'}</span>
                              </button>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleLike(selectedLeaderboardEntry.userId, submission.trickId); }}
                                disabled={likingMap[`${selectedLeaderboardEntry.userId}:${submission.trickId}`]}
                                className="flex items-center gap-1.5 rounded-lg border border-pink-400/20 bg-pink-500/10 px-2.5 py-1 text-xs text-pink-200 transition-colors hover:border-pink-300/40 hover:bg-pink-500/20 disabled:opacity-50"
                              >
                                <Heart size={13} className={likesCache[`${selectedLeaderboardEntry.userId}:${submission.trickId}`] ? 'fill-pink-400 text-pink-400' : ''} />
                                <span>{likesCache[`${selectedLeaderboardEntry.userId}:${submission.trickId}`] ?? submission.likes ?? 0}</span>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {pendingReport && (
                <div className="fixed inset-0 z-[160] flex items-center justify-center bg-black/70 p-4" onClick={closeReportDialog}>
                  <div
                    className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-950 p-5 shadow-2xl"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 rounded-xl border border-orange-400/20 bg-orange-500/10 p-2 text-orange-200">
                        <Flag size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-base font-semibold text-white">填写举报理由</div>
                        <div className="mt-2 text-sm leading-6 text-zinc-300">
                          你正在举报招式：<span className="text-white">{pendingReport.trickName}</span>
                        </div>
                        <div className="mt-1 text-xs leading-5 text-zinc-500">
                          请简要说明举报原因，管理员将在举报管理中看到这条理由。
                        </div>
                      </div>
                    </div>

                    <textarea
                      value={reportReasonInput}
                      onChange={(event) => setReportReasonInput(event.target.value)}
                      placeholder="请输入举报理由，例如：视频与招式不符、明显不是本人录像、内容违规等"
                      rows={4}
                      maxLength={200}
                      className="mt-4 w-full resize-none rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm text-white outline-none transition-colors placeholder:text-zinc-500 focus:border-orange-400/40"
                    />
                    <div className="mt-2 text-right text-[11px] text-zinc-500">
                      {reportReasonInput.trim().length}/200
                    </div>

                    <div className="mt-5 flex gap-3">
                      <button
                        type="button"
                        onClick={closeReportDialog}
                        className="flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-zinc-200 transition-colors hover:bg-white/10"
                      >
                        取消
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (!pendingReport || !reportReasonInput.trim()) return;
                          handleReport(pendingReport.userId, pendingReport.trickId, reportReasonInput.trim());
                        }}
                        disabled={!reportReasonInput.trim() || reportingMap[`${pendingReport.userId}:${pendingReport.trickId}`]}
                        className="flex-1 rounded-xl border border-orange-400/20 bg-orange-500/15 px-4 py-3 text-sm font-semibold text-orange-100 transition-colors hover:bg-orange-500/25 disabled:opacity-50"
                      >
                        {reportingMap[`${pendingReport.userId}:${pendingReport.trickId}`] ? '提交中...' : '提交举报'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

      </div>
    );
  }

  return (
    <div ref={scrollContainerRef} className="h-full w-full overflow-y-auto overflow-x-hidden custom-scrollbar px-3 pt-3 pb-20 md:px-5 md:pb-5 xl:py-4">
      <style>{`
        @keyframes leaderboardSlideIn {
          from { opacity: 0; transform: translateX(60px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes warmup-upload-sheen {
          0% { transform: translateX(-140%); }
          100% { transform: translateX(900%); }
        }
      `}</style>
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-zinc-200 transition-colors hover:border-white/20 hover:bg-white/10"
          >
            <ArrowLeft size={16} />
            返回首页
          </button>
        </div>

        {actionMessage && (
          <div className="rounded-2xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <div className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" />
                  <span className="text-xs font-semibold text-cyan-200">操作通知</span>
                </div>
                <div className="text-sm text-cyan-100 whitespace-pre-line">{actionMessage}</div>
                {processingProgress != null && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-cyan-200/70">{processingLabel || '处理进度'}</span>
                      <span className="text-xs font-mono text-cyan-300">{processingProgress}%</span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-cyan-500/20 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-cyan-400 transition-all duration-300"
                        style={{ width: `${processingProgress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setActionMessage('')}
                className="shrink-0 rounded-lg p-1 text-cyan-400/50 hover:text-white hover:bg-white/10 transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        )}

        <div className="sticky top-0 z-40 -mx-3 px-3 py-2.5 md:-mx-5 md:px-5">
          <div className="relative" ref={searchContainerRef}>
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="搜索选手..."
              className="w-full rounded-xl border border-white/10 bg-zinc-900 pl-9 pr-8 py-2.5 text-xs text-white placeholder:text-zinc-600 outline-none focus:border-cyan-400/30 focus:bg-zinc-800 transition-colors"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => handleSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                <X size={14} />
              </button>
            )}
            {searchQuery && searchMatchId === null && (
              <span className="absolute right-8 top-1/2 -translate-y-1/2 text-[10px] text-zinc-600">无匹配</span>
            )}
            {searchQuery.trim() && (() => {
              const lowerQuery = searchQuery.toLowerCase();
              const suggestions = leaderboard.filter(e =>
                e.displayName.toLowerCase().includes(lowerQuery)
              ).slice(0, 8);
              if (suggestions.length === 0) return null;
              return (
                <div className="absolute left-0 right-0 top-full mt-1 overflow-hidden rounded-xl border border-white/10 bg-zinc-900 shadow-xl shadow-black/50 z-50">
                  {suggestions.map((s) => (
                    <button
                      key={s.userId}
                      type="button"
                      onClick={() => handleSuggestionClick(s)}
                      className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-white/[0.06]"
                    >
                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-white/10 bg-gradient-to-br from-white/10 to-white/[0.02] text-[10px] text-white">
                        {s.avatarUrl ? (
                          <img src={s.avatarUrl} alt="" className="h-full w-full object-cover rounded-md" />
                        ) : (
                          getAvatarInitial(s.displayName)
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-medium text-white">{s.displayName}</div>
                      </div>
                      <span className="shrink-0 text-xs font-orbitron text-yellow-200/60">{s.totalScore}</span>
                    </button>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>

        <section className="grid gap-4 xl:grid-cols-[1.25fr_0.75fr]">
          <div className="relative rounded-[24px] border border-white/10 bg-zinc-950/70 p-3 md:p-4 overflow-hidden">
            {/* Decorative background */}
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(250,204,21,0.04),transparent_50%),radial-gradient(ellipse_at_80%_100%,rgba(6,182,212,0.03),transparent_50%)]" />
            <div className="pointer-events-none absolute inset-0 bg-[length:16px_16px] bg-center opacity-[0.015]" style={{ backgroundImage: 'radial-gradient(circle,rgba(255,255,255,0.8)_1px,transparent_1px)' }} />

            <div className="relative">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
                  <img src={ckoLiveLogo} alt="CKO LIVE" className="h-5 w-auto sm:h-6 object-contain shrink-0" />
                  <h2 className="whitespace-nowrap text-lg font-orbitron font-bold tracking-[0.06em] text-white sm:text-xl sm:tracking-[0.12em]">排行榜</h2>
                </div>
                <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
                  <button
                    type="button"
                    onClick={loadLeaderboard}
                    className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-cyan-400/20 bg-cyan-500/10 px-1.5 py-1.5 text-[11px] text-cyan-100 transition-colors hover:border-cyan-300/40 hover:bg-cyan-400/15 sm:gap-1.5 sm:px-2 sm:text-xs"
                  >
                    <RefreshCcw size={12} className="shrink-0 sm:h-[13px] sm:w-[13px]" />
                    <span className="whitespace-nowrap">刷新</span>
                  </button>
                  <div className="shrink-0 rounded-full border border-yellow-400/20 bg-yellow-500/10 px-2 py-1 text-[10px] font-orbitron uppercase tracking-[0.12em] text-yellow-100 sm:px-2.5 sm:text-[11px] sm:tracking-[0.16em]">
                    Ranked
                  </div>
                </div>
              </div>

            {isLeaderboardLoading ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-8 text-center text-zinc-500">
                排行榜加载中...
              </div>
            ) : leaderboardError ? (
              <div className="rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-4 text-sm text-red-200">
                {leaderboardError}
              </div>
            ) : (
              <div ref={leaderboardListRef} className="flex flex-col gap-2">
                {/* Podium: always show 3 slots */}
                <div className="relative mb-4">
                  {/* Podium base platform */}
                  <div className="absolute bottom-0 left-0 right-0 h-[8px] rounded-b-xl bg-gradient-to-r from-transparent via-white/[0.04] to-transparent" />
                  <div className="relative grid grid-cols-3 items-end gap-2 px-1 pb-3">
                  {/* 2nd place */}
                  {leaderboard[1] ? (() => {
                    const entry = leaderboard[1];
                    const isSelf = entry.userId === user.uid;
                    return (
                      <button
                        key={entry.userId}
                        type="button"
                        data-leaderboard-id={entry.userId}
                        onClick={() => navigate(`/wireless-progress/player/${encodeURIComponent(entry.userId)}`)}
                        style={getLeaderboardCardAnimationStyle(80)}
                        className={`group relative flex flex-col items-center justify-end gap-1.5 rounded-2xl border px-2 py-3 text-center transition-all duration-300 h-[135px] ${
                          isSelf
                            ? 'border-emerald-400/40 bg-gradient-to-b from-emerald-500/12 to-emerald-500/4 shadow-[0_0_15px_rgba(52,211,153,0.12)]'
                            : 'border-zinc-300/15 bg-gradient-to-b from-white/[0.06] to-white/[0.02] hover:border-zinc-300/30 hover:from-white/[0.09] hover:to-white/[0.04] hover:-translate-y-1 shadow-[0_4px_20px_rgba(0,0,0,0.3)]'
                        } ${searchMatchId === entry.userId ? 'ring-2 ring-cyan-400/50 shadow-[0_0_20px_rgba(6,182,212,0.35)]' : ''}`}
                      >
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-300/10 text-[9px] font-orbitron text-zinc-400">2nd</div>
                        <Medal size={14} className="text-zinc-400/50 -mt-0.5" />
                        <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border-2 border-zinc-300/30 bg-gradient-to-br from-white/15 to-white/5 text-sm text-white shadow-[0_0_12px_rgba(180,180,190,0.12)] ring-1 ring-white/5">
                          {entry.avatarUrl ? (
                            <img src={entry.avatarUrl} alt="" className="h-full w-full object-cover" />
                          ) : getAvatarInitial(entry.displayName)}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-xs font-bold text-zinc-200">{entry.displayName}</div>
                          <div className="text-sm font-orbitron text-zinc-100 mt-0.5 drop-shadow-[0_0_4px_rgba(180,180,190,0.2)]">{entry.totalScore}分</div>
                        </div>
                      </button>
                    );
                  })() : (
                    <div
                      style={getLeaderboardCardAnimationStyle(80)}
                      className="relative flex flex-col items-center justify-end gap-1.5 rounded-2xl border border-zinc-300/8 bg-gradient-to-b from-white/[0.02] to-transparent px-2 py-3 text-center h-[135px] opacity-40"
                    >
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-300/10 text-[9px] font-orbitron text-zinc-600">2nd</div>
                      <Medal size={14} className="text-zinc-600/30 -mt-0.5" />
                      <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border-2 border-zinc-300/15 bg-gradient-to-br from-white/5 to-transparent text-sm text-zinc-600">—</div>
                      <div className="text-[11px] text-zinc-600">虚位以待</div>
                    </div>
                  )}

                  {/* 1st place */}
                  {leaderboard[0] ? (() => {
                    const entry = leaderboard[0];
                    const isSelf = entry.userId === user.uid;
                    return (
                      <button
                        key={entry.userId}
                        type="button"
                        data-leaderboard-id={entry.userId}
                        onClick={() => navigate(`/wireless-progress/player/${encodeURIComponent(entry.userId)}`)}
                        style={getLeaderboardCardAnimationStyle(0)}
                        className={`group relative flex flex-col items-center justify-end gap-2 rounded-2xl border px-2 py-3 text-center transition-all duration-300 h-[175px] ${
                          isSelf
                            ? 'border-emerald-400/40 bg-gradient-to-b from-emerald-500/14 to-emerald-500/6 shadow-[0_0_25px_rgba(52,211,153,0.18)]'
                            : 'border-yellow-400/30 bg-gradient-to-b from-yellow-500/10 via-yellow-500/6 to-yellow-500/2 hover:border-yellow-400/50 hover:from-yellow-500/15 hover:-translate-y-1 shadow-[0_0_30px_rgba(250,204,21,0.15),0_8px_30px_rgba(0,0,0,0.4)]'
                        } ${searchMatchId === entry.userId ? 'ring-2 ring-cyan-400/50 shadow-[0_0_20px_rgba(6,182,212,0.35)]' : ''}`}
                      >
                        {/* Crown on top */}
                        <Crown size={20} className="absolute -top-3 text-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.7)]" />
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-yellow-400/20 text-[9px] font-orbitron text-yellow-300 shadow-[0_0_10px_rgba(250,204,21,0.2)]">1st</div>
                        <Trophy size={18} className="text-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.6)] -mt-0.5" />
                        <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-full border-[3px] border-yellow-400/50 bg-gradient-to-br from-white/20 to-white/5 text-lg text-white shadow-[0_0_22px_rgba(250,204,21,0.3)] ring-1 ring-yellow-400/20">
                          {entry.avatarUrl ? (
                            <img src={entry.avatarUrl} alt="" className="h-full w-full object-cover" />
                          ) : getAvatarInitial(entry.displayName)}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-sm font-extrabold text-yellow-100 drop-shadow-[0_0_6px_rgba(250,204,21,0.4)]">{entry.displayName}</div>
                          <div className="text-xl font-orbitron font-black text-yellow-200 mt-1 drop-shadow-[0_0_8px_rgba(250,204,21,0.5)]">{entry.totalScore}分</div>
                        </div>
                      </button>
                    );
                  })() : (
                    <div
                      style={getLeaderboardCardAnimationStyle(0)}
                      className="relative flex flex-col items-center justify-end gap-2 rounded-2xl border border-yellow-400/10 bg-gradient-to-b from-yellow-500/3 to-transparent px-2 py-3 text-center h-[175px] opacity-40"
                    >
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-yellow-400/10 text-[9px] font-orbitron text-yellow-600/40">1st</div>
                      <Trophy size={18} className="text-yellow-600/20 -mt-0.5" />
                      <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-full border-[3px] border-yellow-400/15 bg-gradient-to-br from-white/5 to-transparent text-lg text-zinc-600">—</div>
                      <div className="text-[12px] text-zinc-600">虚位以待</div>
                    </div>
                  )}

                  {/* 3rd place */}
                  {leaderboard[2] ? (() => {
                    const entry = leaderboard[2];
                    const isSelf = entry.userId === user.uid;
                    return (
                      <button
                        key={entry.userId}
                        type="button"
                        data-leaderboard-id={entry.userId}
                        onClick={() => navigate(`/wireless-progress/player/${encodeURIComponent(entry.userId)}`)}
                        style={getLeaderboardCardAnimationStyle(160)}
                        className={`group relative flex flex-col items-center justify-end gap-1.5 rounded-2xl border px-2 py-3 text-center transition-all duration-300 h-[120px] ${
                          isSelf
                            ? 'border-emerald-400/40 bg-gradient-to-b from-emerald-500/12 to-emerald-500/4 shadow-[0_0_15px_rgba(52,211,153,0.12)]'
                            : 'border-amber-700/25 bg-gradient-to-b from-amber-500/8 to-amber-500/2 hover:border-amber-600/35 hover:from-amber-500/12 hover:-translate-y-1 shadow-[0_4px_20px_rgba(0,0,0,0.3)]'
                        } ${searchMatchId === entry.userId ? 'ring-2 ring-cyan-400/50 shadow-[0_0_20px_rgba(6,182,212,0.35)]' : ''}`}
                      >
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-700/20 text-[9px] font-orbitron text-amber-200">3rd</div>
                        <Medal size={14} className="text-amber-500/50 -mt-0.5" />
                        <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border-2 border-amber-600/30 bg-gradient-to-br from-white/15 to-white/5 text-sm text-white shadow-[0_0_12px_rgba(217,119,6,0.12)] ring-1 ring-white/5">
                          {entry.avatarUrl ? (
                            <img src={entry.avatarUrl} alt="" className="h-full w-full object-cover" />
                          ) : getAvatarInitial(entry.displayName)}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-xs font-bold text-amber-100">{entry.displayName}</div>
                          <div className="text-sm font-orbitron text-amber-200 mt-0.5 drop-shadow-[0_0_4px_rgba(217,119,6,0.15)]">{entry.totalScore}分</div>
                        </div>
                      </button>
                    );
                  })() : (
                    <div
                      style={getLeaderboardCardAnimationStyle(160)}
                      className="relative flex flex-col items-center justify-end gap-1.5 rounded-2xl border border-amber-700/10 bg-gradient-to-b from-amber-500/2 to-transparent px-2 py-3 text-center h-[120px] opacity-40"
                    >
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-700/10 text-[9px] font-orbitron text-zinc-600">3rd</div>
                      <Medal size={14} className="text-zinc-600/30 -mt-0.5" />
                      <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border-2 border-amber-600/10 bg-gradient-to-br from-white/5 to-transparent text-sm text-zinc-600">—</div>
                      <div className="text-[11px] text-zinc-600">虚位以待</div>
                    </div>
                  )}
                  </div>
                  {/* Podium connector glow */}
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-[1px] bg-gradient-to-r from-transparent via-yellow-400/15 to-transparent" />
                </div>

                {/* Rank 4+: show all entries, pad with placeholders up to 20 */}
                {(() => {
                  const totalEntries = leaderboard.length;
                  const minRows = Math.max(17, totalEntries - 3); // at least 17 rows (20 - 3 podium), or more if entries exceed 20
                  const rows = Array.from({ length: minRows }, (_, i) => {
                    const rank = i + 4;
                    const entry = leaderboard[i + 3];
                    const animDelay = i < 17 ? 240 + i * 70 : 240 + i * 70;
                    if (entry) {
                      const isSelf = entry.userId === user.uid;
                      const progressPct = Math.min(100, Math.round((entry.totalScore / TOTAL_AVAILABLE_SCORE) * 100));
                      return (
                        <div
                          key={`${entry.userId}-v${leaderboardVersion}`}
                          data-leaderboard-id={entry.userId}
                          className="flex items-center gap-2"
                          style={getLeaderboardCardAnimationStyle(animDelay)}
                        >
                          <span className={`shrink-0 w-6 text-center text-xs font-orbitron font-bold ${
                            rank === 4 ? 'text-yellow-300' :
                            rank === 5 ? 'text-zinc-300' :
                            'text-zinc-500'
                          }`}>
                            {rank}
                          </span>
                          <button
                            type="button"
                            onClick={() => navigate(`/wireless-progress/player/${encodeURIComponent(entry.userId)}`)}
                            className={`group flex flex-1 items-center justify-between gap-2 rounded-lg border px-2.5 py-1 text-left transition-all duration-200 ${
                              isSelf
                                ? 'border-emerald-400/30 bg-gradient-to-r from-emerald-500/8 to-transparent shadow-[0_0_14px_rgba(52,211,153,0.1)]'
                                : 'border-white/5 bg-gradient-to-r from-white/[0.03] to-transparent hover:border-white/12 hover:from-white/[0.06] hover:shadow-[0_2px_12px_rgba(0,0,0,0.2)]'
                            } ${searchMatchId === entry.userId ? '!border-cyan-400/50 shadow-[0_0_16px_rgba(6,182,212,0.3)]' : ''}`}
                          >
                            <div className="flex min-w-0 flex-1 items-center gap-2">
                              <div className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-md border border-white/10 bg-gradient-to-br from-white/10 to-white/[0.02] text-[10px] text-white">
                                {entry.avatarUrl ? (
                                  <img src={entry.avatarUrl} alt={`${entry.displayName} 头像`} className="h-full w-full object-cover" />
                                ) : (
                                  getAvatarInitial(entry.displayName)
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <div className="truncate text-xs font-semibold text-white">{entry.displayName}</div>
                                  {isSelf && (
                                    <span className="shrink-0 rounded-md border border-emerald-400/20 bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-orbitron text-emerald-200">YOU</span>
                                  )}
                                </div>
                                <div className="mt-0.5 flex items-center gap-2">
                                  <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/[0.06]">
                                    <div
                                      className="h-full rounded-full bg-gradient-to-r from-yellow-500/70 to-yellow-400/70 transition-all duration-500"
                                      style={{ width: `${progressPct}%` }}
                                    />
                                  </div>
                                  <span className="shrink-0 text-[10px] text-zinc-600 tabular-nums">{progressPct}%</span>
                                </div>
                                <div className="text-[10px] text-zinc-600">{entry.completedCount}项</div>
                              </div>
                            </div>
                            <div className="shrink-0 text-right">
                              <div className="text-xs font-orbitron font-bold text-yellow-200/80 drop-shadow-[0_0_3px_rgba(250,204,21,0.1)]">{entry.totalScore}</div>
                            </div>
                          </button>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={`placeholder-${rank}-v${leaderboardVersion}`}
                        style={getLeaderboardCardAnimationStyle(animDelay)}
                        className="flex items-center gap-2 opacity-30"
                      >
                        <span className="shrink-0 w-6 text-center text-xs font-orbitron font-bold text-zinc-700">{rank}</span>
                        <div className="flex flex-1 items-center justify-between gap-2 rounded-lg border border-white/[0.03] bg-white/[0.01] px-2.5 py-1.5">
                          <span className="text-[11px] text-zinc-700">虚位以待</span>
                          <span className="text-[11px] font-orbitron text-zinc-700">—</span>
                        </div>
                      </div>
                    );
                  });
                  return rows;
                })()}
              </div>
            )}
            </div>
          </div>

          <div className="hidden flex-col gap-4 xl:flex xl:relative">
            <div className="rounded-[24px] border border-white/10 bg-zinc-950/70 p-3 md:p-4 xl:sticky xl:bottom-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-xl font-orbitron font-bold tracking-[0.12em] text-white">我的招式页</h2>
                </div>
                <div className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-2.5 py-1 text-[11px] font-orbitron uppercase tracking-[0.16em] text-cyan-100">
                  Page
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-black/20 p-3">
                <div className="grid gap-2.5">
                  <div className="grid grid-cols-2 gap-2.5">
                    <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                      <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">完成进度</div>
                      <div className="mt-1 text-sm text-white">{completedCount} / {WARMUP_TRICK_COUNT}</div>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                      <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">当前总分</div>
                      <div className="mt-1 text-sm text-white">{totalScore}</div>
                    </div>
                  </div>

                  <div className="text-sm text-zinc-400">
                    {profile.isRegistered ? '已报名，可直接进入上传页继续提交。' : '未报名时也可先进入查看上传说明。'}
                  </div>

                  <button
                    type="button"
                    onClick={() => navigate('/wireless-progress/my-tricks')}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-cyan-300/30 bg-cyan-500/15 px-4 py-3 text-sm font-orbitron uppercase tracking-[0.14em] text-cyan-50 transition-all hover:border-cyan-200/50 hover:bg-cyan-400/20"
                  >
                    <Upload size={16} />
                    进入我的招式页
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile fixed bottom bar */}
          <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-white/10 bg-zinc-950 px-3 py-3.5 xl:hidden">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="text-xs text-zinc-500">完成</div>
                <div className="text-sm font-semibold text-white">{completedCount}/{WARMUP_TRICK_COUNT}</div>
                <div className="text-xs text-zinc-500">总分</div>
                <div className="text-sm font-semibold text-emerald-300">{totalScore}</div>
              </div>
              <button
                type="button"
                onClick={() => navigate('/wireless-progress/my-tricks')}
                className="inline-flex items-center gap-1.5 rounded-xl border border-cyan-300/30 bg-cyan-500/15 px-4 py-3 text-xs font-orbitron uppercase tracking-[0.10em] text-cyan-50 active:bg-cyan-400/20"
              >
                <Upload size={14} />
                我的招式
              </button>
            </div>
          </div>
        </section>

        <div
          className={`fixed z-50 touch-none select-none ${floatPos.x ? '' : canAccessWarmupAdmin(user) ? 'bottom-[186px] xl:bottom-[126px]' : 'bottom-[132px] xl:bottom-[70px]'}`}
          style={{
            left: floatPos.x || undefined,
            top: floatPos.y || undefined,
            right: floatPos.x ? undefined : 16,
            bottom: floatPos.x ? undefined : undefined,
          }}
          onMouseDown={handleFloatMouseDown}
          onTouchStart={handleFloatTouchStart}
        >
          <button
            type="button"
            onClick={handleFloatClick}
            className={`relative flex items-center justify-center rounded-full border shadow-lg transition-all active:scale-95 ${
              profile.isRegistered
                ? 'h-11 w-11 border-emerald-400/30 bg-emerald-600 text-white hover:bg-emerald-500'
                : 'h-11 w-11 border-emerald-400/30 bg-emerald-600 text-white hover:bg-emerald-500'
            }`}
          >
            <Trophy size={18} />
            {profile.isRegistered && (
              <span className="absolute -top-1 -right-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-yellow-500 px-1 text-[9px] font-bold text-black leading-none">
                {totalScore}
              </span>
            )}
          </button>
        </div>

        {/* Admin button - floating */}
        {canAccessWarmupAdmin(user) && (
          <button
            type="button"
            onClick={() => navigate('/wireless-progress/admin')}
            className="fixed z-50 right-4 bottom-[130px] xl:bottom-[70px] flex h-11 w-11 items-center justify-center rounded-2xl border border-red-400/30 bg-red-600/80 text-white shadow-lg backdrop-blur-sm transition-all hover:bg-red-500/80 hover:border-red-300/50 active:scale-95"
          >
            <Flag size={18} />
          </button>
        )}

        {/* Back to top button */}
        <button
          type="button"
          onClick={scrollToTop}
          className="fixed z-50 right-4 bottom-[80px] xl:bottom-[18px] flex h-10 w-10 items-center justify-center rounded-2xl border-2 border-zinc-600 bg-zinc-800/90 text-white shadow-lg backdrop-blur-sm transition-all hover:bg-zinc-700/90 hover:border-zinc-400 active:scale-95"
        >
          <ChevronUp size={20} />
        </button>

        {isHeroCardExpanded && (
          <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${shouldReduceMobileVisualEffects ? 'bg-black/88' : 'bg-black/60 backdrop-blur-sm'}`} onClick={() => setIsHeroCardExpanded(false)}>
            <div
              className="w-full max-w-[380px] max-h-[80vh] overflow-y-auto overflow-x-hidden rounded-2xl border border-zinc-800 bg-zinc-950 p-4 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {!profile.isRegistered ? (
                <>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-orbitron uppercase tracking-[0.2em] text-emerald-200">
                      <Trophy size={14} />
                      CKO2026预热赛
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsHeroCardExpanded(false)}
                      className={`inline-flex items-center justify-center h-10 w-10 rounded-2xl border-2 border-zinc-600 text-white shadow-lg transition-all hover:bg-zinc-700/90 hover:border-zinc-400 active:scale-95 ${shouldReduceMobileVisualEffects ? 'bg-zinc-800' : 'bg-zinc-800/90 backdrop-blur-sm'}`}
                    >
                      <X size={18} />
                    </button>
                  </div>

                  <h2 className="text-lg font-orbitron font-black tracking-[0.10em] text-white">{CKO2026_WARMUP_MODE_NAME}</h2>
                  <p className="mt-1.5 text-xs leading-5 text-zinc-400">
                    以 CKO2026 中国剑玉杯招式表为基础，报名后即可进入排行榜。上传招式视频即计分，总分公开展示。
                  </p>

                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="rounded-xl border border-white/10 bg-zinc-900 px-2.5 py-2">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">总招式</div>
                      <div className="mt-1 text-base font-orbitron text-white">{WARMUP_TRICK_COUNT}</div>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-zinc-900 px-2.5 py-2">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">满分</div>
                      <div className="mt-1 text-base font-orbitron text-yellow-300">{TOTAL_AVAILABLE_SCORE}</div>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="flex items-center gap-2 mb-2">
                      <Medal size={14} className="text-yellow-300" />
                      <span className="text-xs font-semibold text-white">模式流程</span>
                    </div>
                    <div className="grid gap-1.5">
                      {[
                        { icon: UserPlus, title: '报名进入', desc: '点击报名按钮正式加入预热赛。' },
                        { icon: Upload, title: '上传招式', desc: '在个人招式页为对应招式上传视频。' },
                        { icon: PlayCircle, title: '累计得分', desc: '每个招式按原始分值累计个人总分。' },
                        { icon: Trophy, title: '公开排行', desc: '排行榜看总分，选手详情页看视频。' }
                      ].map((item) => {
                        const Icon = item.icon;
                        return (
                          <div key={item.title} className="grid grid-cols-[32px_minmax(0,1fr)] items-start gap-2 rounded-xl border border-white/10 bg-zinc-900 p-2">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-cyan-400/20 bg-cyan-500/10 text-cyan-200">
                              <Icon size={13} />
                            </div>
                            <div>
                              <div className="text-xs font-semibold text-white">{item.title}</div>
                              <div className="text-[11px] leading-4 text-zinc-500">{item.desc}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleRegister}
                    disabled={isRegistering}
                    className="mt-3 inline-flex w-full items-center justify-center gap-2.5 rounded-xl border border-emerald-300/40 bg-emerald-600 py-3 text-sm font-orbitron uppercase tracking-[0.14em] text-white shadow-lg transition-all hover:border-emerald-200/60 hover:bg-emerald-500 active:scale-[0.98]"
                  >
                    <UserPlus size={18} />
                    {isRegistering ? '报名中...' : '报名参加 CKO2026 预热赛'}
                  </button>
                </>
              ) : (
                <>
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/25 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-orbitron uppercase tracking-[0.14em] text-emerald-200">
                        <Trophy size={12} />
                        CKO2026
                      </div>
                      <div className="inline-flex items-center gap-1 rounded-full border border-emerald-400/25 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-orbitron uppercase tracking-[0.12em] text-emerald-200">
                        <CheckCircle2 size={11} />
                        已报名
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsHeroCardExpanded(false)}
                      className={`inline-flex items-center justify-center h-10 w-10 rounded-2xl border-2 border-zinc-600 text-white shadow-lg transition-all hover:bg-zinc-700/90 hover:border-zinc-400 active:scale-95 ${shouldReduceMobileVisualEffects ? 'bg-zinc-800' : 'bg-zinc-800/90 backdrop-blur-sm'}`}
                    >
                      <X size={18} />
                    </button>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-300 mb-3">
                    <span>完成 {completedCount} / {WARMUP_TRICK_COUNT}</span>
                    <span>总分 {totalScore}</span>
                    <span>{formatTime(profile.registeredAt)}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-xl border border-white/10 bg-zinc-900 px-2.5 py-2">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">总招式</div>
                      <div className="mt-1 text-base font-orbitron text-white">{WARMUP_TRICK_COUNT}</div>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-zinc-900 px-2.5 py-2">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">满分</div>
                      <div className="mt-1 text-base font-orbitron text-yellow-300">{TOTAL_AVAILABLE_SCORE}</div>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="flex items-center gap-2 mb-2">
                      <Medal size={14} className="text-yellow-300" />
                      <span className="text-xs font-semibold text-white">模式流程</span>
                    </div>
                    <div className="grid gap-1.5">
                      {[
                        { icon: UserPlus, title: '报名进入', desc: '点击报名按钮正式加入预热赛。' },
                        { icon: Upload, title: '上传招式', desc: '在个人招式页为对应招式上传视频。' },
                        { icon: PlayCircle, title: '累计得分', desc: '每个招式按原始分值累计个人总分。' },
                        { icon: Trophy, title: '公开排行', desc: '排行榜看总分，选手详情页看视频。' }
                      ].map((item) => {
                        const Icon = item.icon;
                        return (
                          <div key={item.title} className="grid grid-cols-[32px_minmax(0,1fr)] items-start gap-2 rounded-xl border border-white/10 bg-zinc-900 p-2">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-cyan-400/20 bg-cyan-500/10 text-cyan-200">
                              <Icon size={13} />
                            </div>
                            <div>
                              <div className="text-xs font-semibold text-white">{item.title}</div>
                              <div className="text-[11px] leading-4 text-zinc-500">{item.desc}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        </div>
      </div>
  );
}

export default CKO2026WarmupMode;
